<template>
	<div class="container-fluid">
		<!-- 헤더 -->
		<div class="row">
			<div class="col-lg-12">
				<div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
					<div class="text-left">
						<h4 class="mb-1">노출추적</h4>
						<small class="text-muted">
							일별 노출/클릭/CTR + 어제 대비 변화. [수집] = 7일 키워드 발견 + 30일 데일리 누적.
						</small>
					</div>
					<div class="d-flex align-items-center flex-wrap">
						<!-- 보기 모드 (자주 사용) -->
						<b-button-group size="sm" class="mr-2 mb-1" v-if="!isOrderEditMode">
							<b-button :variant="viewMode === 'detail' ? 'secondary' : 'outline-secondary'"
								@click="viewMode = 'detail'" v-b-tooltip.hover title="키워드별 상세 + 상품 통계">
								<i class="ri-table-line mr-1"></i>상세
							</b-button>
							<b-button :variant="viewMode === 'summary' ? 'secondary' : 'outline-secondary'"
								@click="viewMode = 'summary'" v-b-tooltip.hover title="상품 단위 클릭/판매만">
								<i class="ri-list-check mr-1"></i>요약
							</b-button>
						</b-button-group>

						<!-- 순서 편집 모드 인라인 툴바 -->
						<template v-if="isOrderEditMode">
							<b-button-group size="sm" class="mr-2 mb-1">
								<b-button variant="outline-secondary"
									:disabled="!selectedProductForOrder && !selectedKeywordForOrder"
									@click="moveSelected('top')" v-b-tooltip.hover title="맨 위로">▲</b-button>
								<b-button variant="outline-secondary"
									:disabled="!selectedProductForOrder && !selectedKeywordForOrder"
									@click="moveSelected('up')" v-b-tooltip.hover title="위로">△</b-button>
								<b-button variant="outline-secondary"
									:disabled="!selectedProductForOrder && !selectedKeywordForOrder"
									@click="moveSelected('down')" v-b-tooltip.hover title="아래로">▽</b-button>
								<b-button variant="outline-secondary"
									:disabled="!selectedProductForOrder && !selectedKeywordForOrder"
									@click="moveSelected('bottom')" v-b-tooltip.hover title="맨 아래로">▼</b-button>
							</b-button-group>
							<b-button size="sm" variant="primary" class="mr-1 mb-1" @click="saveOrder">
								<i class="ri-save-line mr-1"></i>순서 저장
							</b-button>
							<b-button size="sm" variant="danger" class="mr-2 mb-1" @click="cancelOrder">
								편집 취소
							</b-button>
						</template>

						<!-- 수집 (자주 사용) -->
						<b-button v-if="tableSelected.length > 0 && !isOrderEditMode" variant="primary" class="mr-2 mb-1"
							:disabled="isCollecting" @click="bulkCollect">
							<i class="ri-download-cloud-2-line mr-1"></i>
							선택 수집 ({{ tableSelected.length }})
						</b-button>
						<b-button v-if="!isOrderEditMode" variant="success" class="mr-2 mb-1"
							:disabled="isCollecting || tableItems.length === 0"
							@click="collectAllItems">
							<i class="ri-download-cloud-2-line mr-1"></i>전체 수집 ({{ tableItems.length }})
						</b-button>

						<!-- 설정 드롭다운 (가끔 사용: 상품수, 순서편집, 초기화) -->
						<b-dropdown v-if="!isOrderEditMode" size="sm" variant="light"
							right no-caret class="mb-1 settings-dropdown" :disabled="isCollecting"
							v-b-tooltip.hover title="설정 (상품수 / 순서편집 / 초기화)">
							<template #button-content>
								<i class="ri-settings-3-line text-secondary" style="font-size: 16px;"></i>
							</template>
							<b-dropdown-form v-if="activeTabIndex === 0" form-class="topn-form"
								v-b-tooltip.hover.left
								title="내 상품 표시·수집 상한 (7일 판매량 desc). 0 또는 전체 상품수보다 큰 값은 전체로 자동 보정">
								<div class="d-flex align-items-center">
									<i class="ri-hashtag mr-2 text-muted"></i>
									<span class="flex-grow-1">상품 수 입력</span>
									<input v-model.number="topN" type="number" min="0"
										class="form-control form-control-sm" style="width: 80px;" />
								</div>
							</b-dropdown-form>
							<b-dropdown-divider v-if="activeTabIndex === 0" />
							<b-dropdown-form form-class="topn-form"
								v-b-tooltip.hover.left
								title="ON이면 [수집] 시 각 키워드의 Top10 순위 + 그 상품들의 30일 노출/클릭까지 같이 가져옴. 키워드 수×2 호출 추가되어 많이 느려짐">
								<div class="d-flex align-items-center">
									<i class="ri-trophy-line mr-2 text-muted"></i>
									<span class="flex-grow-1">심층 경쟁분석</span>
									<input type="checkbox" v-model="collectRanking" />
								</div>
							</b-dropdown-form>
							<b-dropdown-divider />
							<b-dropdown-item @click="enterOrderEditMode">
								<i class="ri-list-ordered mr-1"></i>순서 편집
							</b-dropdown-item>
							<b-dropdown-divider />
							<b-dropdown-item v-if="selectedProductCount > 0" class="text-danger" @click="clearSelectedData">
								<i class="ri-delete-bin-line mr-1"></i>선택 {{ selectedProductCount }}개 초기화
							</b-dropdown-item>
							<b-dropdown-item class="text-danger" @click="clearAllData">
								<i class="ri-delete-bin-2-line mr-1"></i>전체 초기화
							</b-dropdown-item>
						</b-dropdown>
					</div>
				</div>
			</div>

			<!-- 상단 통합 통계 그래프 (현재 탭 기준) -->
			<div class="col-lg-12 mb-3" v-if="!loading && tableItems.length > 0">
				<div class="card">
					<div class="card-body py-2">
						<div class="d-flex justify-content-between align-items-center mb-1">
							<small class="text-muted">
								<b>{{ activeTabIndex === 0 ? '내 상품' : '경쟁 상품' }} 합산</b> — 30일 노출/클릭 추이
							</small>
							<small class="text-muted">{{ datesinit[0] }} ~ {{ datesinit[datesinit.length - 1] }}</small>
						</div>
						<ApexChart element="exposure-summary-chart" :chartOption="summaryChartOptions" />
					</div>
				</div>
			</div>

			<!-- 탭 + 경쟁상품 추가 -->
			<div class="col-lg-12">
				<div class="mb-3 d-flex justify-content-between align-items-center flex-wrap">
					<b-button-group>
						<b-button :variant="activeTabIndex === 0 ? 'primary' : 'outline-primary'"
							@click="onChangeTab(0)" :disabled="isOrderEditMode">
							내 상품 ({{ mineProducts.length }})
						</b-button>
						<b-button :variant="activeTabIndex === 1 ? 'primary' : 'outline-primary'"
							@click="onChangeTab(1)" :disabled="isOrderEditMode">
							경쟁 상품 ({{ competitorProducts.length }})
						</b-button>
					</b-button-group>

					<div v-if="activeTabIndex === 1 && !isOrderEditMode" class="d-flex align-items-center">
						<input v-model="competitorUrl" type="text"
							class="form-control form-control-sm mr-2"
							style="min-width: 360px;"
							placeholder="쿠팡 상품 URL (https://www.coupang.com/vp/products/...?vendorItemId=...)" />
						<b-button size="sm" variant="primary" @click="addCompetitor" :disabled="isCollecting">
							<i class="ri-add-line mr-1"></i>경쟁상품 추가
						</b-button>
					</div>
				</div>

				<!-- 진행 상태 -->
				<div v-if="isCollecting" class="alert alert-info py-2 mb-3">
					<i class="ri-loader-4-line mr-1"></i>{{ progressMessage }}
				</div>
				<div v-if="isOrderEditMode" class="alert alert-warning py-2 mb-3">
					<i class="ri-information-line mr-1"></i>
					편집 모드 — 상품 또는 키워드를 라디오로 선택한 뒤 화살표로 이동하세요. [순서 저장] 또는 [편집 취소].
				</div>

				<!-- CDataTable -->
				<div class="rounded mb-3">
					<CDataTable
						storage-key="exposureTracking.1" v-if="!loading"
						ref="dataTable"
						:headers="tableHeaders"
						:items="tableItems"
						:height="'700px'"
						:show-select="!isOrderEditMode"
						:item-key="'rowKey'"
						:fixed-columns="'rankChanges'"
						v-model="tableSelected">

						<!-- 상품정보 (클릭 시 디테일 모달) -->
						<template v-slot:productInfo="{ item }">
							<div class="d-flex align-items-center" style="min-width:240px;">
								<input v-if="isOrderEditMode" type="radio" name="prod-select" class="mr-2"
									:checked="selectedProductForOrder === item.productId"
									@click="selectProductForOrder(item.productId)" />
								<img v-if="item.image" :src="cdnImage(item.image)" class="img-fluid rounded mr-2 product-thumb"
									style="width:60px;height:60px;object-fit:cover;cursor:pointer;" alt="thumb"
									@click="openProductModal(item)">
								<div>
									<div style="font-size:90%;line-height:130%;">
										<a href="javascript:void(0)" @click="openProductModal(item)" class="product-name-link"
											v-b-tooltip.hover title="상세 (옵션별 + 통합 그래프) 보기">
											{{ item.itemName || ('상품 ' + item.productId) }}
										</a>
										<span v-if="item.optionCount > 0" class="badge bg-light text-dark ml-1"
											style="font-size:75%;">옵션 {{ item.optionCount }}</span>
									</div>
									<small class="text-muted d-block">노출ID: {{ item.productId }}</small>
									<small v-if="item.vendorItemId" class="text-muted d-block">대표옵션: {{ item.vendorItemId }}</small>
									<small class="text-muted d-block" v-if="item.lastFetchedAt">
										최근수집: {{ formatDateTime(item.lastFetchedAt) }}
									</small>
									<div class="mt-1" v-if="!isOrderEditMode">
										<a href="javascript:void(0)" @click="promptAddKeyword(item)"
											:class="{ 'disabled': isCollecting }" style="font-size:85%;">
											<i class="ri-add-line"></i> 키워드 추가
										</a>
										<a v-if="item.ownership === 'competitor'" href="javascript:void(0)"
											class="text-danger ml-2" style="font-size:85%;"
											@click="removeCompetitor(item)" :class="{ 'disabled': isCollecting }">
											<i class="ri-delete-bin-line"></i> 경쟁상품 제거
										</a>
									</div>
								</div>
							</div>
						</template>

						<!-- 키워드 (호버 시 우측 X 아이콘 + 편집모드 라디오) -->
						<template v-slot:keywords="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-if="item.keywords.length === 0" class="text-muted text-center py-2"
									style="font-size:85%;">
									[수집]을 눌러 시작하세요.
								</div>
								<div v-for="(kw, kIdx) in item.keywords" :key="kIdx" class="kw-row"
									style="font-size:85%;" :title="kw.keyword">
									<input v-if="isOrderEditMode" type="radio" name="kw-select" class="mr-1"
										:checked="isKeywordSelected(item.productId, kw.keyword)"
										@click="selectKeywordForOrder(item.productId, kw.keyword)" />
									<span v-if="kw.isNew" class="badge bg-warning mr-1">🆕</span>
									<span v-else-if="kw.keywordType === 'tracked'" class="badge bg-info mr-1">★</span>
									{{ kw.keyword }}
									<i v-if="!isOrderEditMode" class="ri-close-line kw-delete"
										@click="deleteKeyword(item, kw)"
										v-b-tooltip.hover title="키워드 삭제"></i>
								</div>
							</template>
							<!-- 상품 통계 라벨 (상세/요약 둘 다) -->
							<div class="kw-row stat-row" style="font-size:85%;">
								<span class="badge bg-primary">클릭</span>
							</div>
							<div class="kw-row stat-row" style="font-size:85%;">
								<span class="badge bg-success">판매</span>
							</div>
						</template>

						<!-- 순위 (Top 10 안 내 순위 또는 외) -->
						<template v-slot:rank="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-for="(kw, kIdx) in item.keywords" :key="kIdx"
									class="kw-row text-center" style="font-size:80%;">
									<template v-if="getKeywordRankInfo(item, kw.keyword)">
										<span v-if="getMyRank(item, kw.keyword)" class="badge bg-primary"
											v-b-tooltip.hover.html :title="rankTooltip(item, kw.keyword).replace(/\n/g, '<br>')">
											{{ getMyRank(item, kw.keyword) }}위
										</span>
										<span v-else class="badge bg-secondary"
											v-b-tooltip.hover.html :title="rankTooltip(item, kw.keyword).replace(/\n/g, '<br>')">
											외
										</span>
									</template>
									<span v-else class="text-muted">-</span>
								</div>
							</template>
							<div class="kw-row stat-row text-center">-</div>
							<div class="kw-row stat-row text-center">-</div>
						</template>

						<!-- 변화 (어제 vs 오늘) - 가로 stacked -->
						<template v-slot:rankChanges="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-for="(kw, kIdx) in item.keywords" :key="kIdx"
									class="kw-row text-right" style="font-size:80%;">
									<span class="metric-inline">
										<span class="m-imp" :class="changeColor(kw.change.impression)">{{ formatChange(kw.change.impression, 'impression') }}</span>
										<span class="m-clk" :class="changeColor(kw.change.srpClick)">{{ formatChange(kw.change.srpClick, 'srpClick') }}</span>
										<span class="m-ctr" :class="changeColor(kw.change.ctr)">{{ formatChange(kw.change.ctr, 'ctr') }}</span>
									</span>
								</div>
							</template>
							<div class="kw-row stat-row text-right" style="font-size:80%;"
								:class="changeColor(getProductStatChange(item, 'click'))">
								{{ formatChange(getProductStatChange(item, 'click'), 'click') }}
							</div>
							<div class="kw-row stat-row text-right" style="font-size:80%;"
								:class="changeColor(getProductStatChange(item, 'sale'))">
								{{ formatChange(getProductStatChange(item, 'sale'), 'sale') }}
							</div>
						</template>

						<!-- 동적 날짜 컬럼 - 가로 stacked -->
						<template v-for="(date, dIdx) in dates" v-slot:[`date_${date}`]="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-for="(kw, kIdx) in item.keywords"
									:key="`${item.productId}-${kIdx}-${dIdx}`"
									class="kw-row text-right" style="font-size:80%;">
									<span class="metric-inline">
										<span class="m-imp">{{ getCellMulti(kw, datesinit[dIdx]).imp }}</span>
										<span class="m-clk">{{ getCellMulti(kw, datesinit[dIdx]).clk }}</span>
										<span class="m-ctr">{{ getCellMulti(kw, datesinit[dIdx]).ctr }}</span>
									</span>
								</div>
							</template>
							<div :key="`stat-click-${item.rowKey}-${dIdx}`"
								class="kw-row stat-row text-right">
								{{ getProductStat(item, datesinit[dIdx], 'click') }}
							</div>
							<div :key="`stat-sale-${item.rowKey}-${dIdx}`"
								class="kw-row stat-row text-right">
								{{ getProductStat(item, datesinit[dIdx], 'sale') }}
							</div>
						</template>

						<!-- 차트 -->
						<template v-slot:chartBtn="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-for="(kw, kIdx) in item.keywords" :key="kIdx" class="kw-row text-center"
									style="font-size:85%;">
									<a href="javascript:void(0)" v-if="kw.dailyDataLength > 0"
										@click="openChart(item, kw)">
										<i class="ri-line-chart-line"></i>
									</a>
									<span v-else class="text-muted">-</span>
								</div>
							</template>
							<div class="kw-row stat-row text-center">-</div>
							<div class="kw-row stat-row text-center">-</div>
						</template>
					</CDataTable>

					<div v-if="loading" class="text-center py-5 text-muted">로딩 중...</div>
					<div v-else-if="tableItems.length === 0" class="text-center py-5 text-muted">
						<span v-if="activeTabIndex === 0">GrowthSales/GrowthDB에 등록된 상품이 없습니다.</span>
						<span v-else>추가된 경쟁 상품이 없습니다. 위에 URL을 입력해 주세요.</span>
					</div>
				</div>
			</div>
		</div>

		<!-- 차트 모달 (단일 키워드 추이) -->
		<b-modal id="exposureChartModal" :title="chartTitle" size="xl" hide-footer>
			<ApexChart v-if="chartOptions" element="exposure-keyword-chart" :chartOption="chartOptions" />
			<div v-else class="text-center py-5 text-muted">데이터가 없습니다.</div>
		</b-modal>

		<!-- 상품 상세 모달 (옵션별 + 통합 그래프) -->
		<b-modal id="productDetailModal" size="xl" hide-footer
			modal-class="modal-fullscreen-90"
			:title="selectedProductForModal ? (selectedProductForModal.itemName || ('상품 ' + selectedProductForModal.productId)) : ''">
			<div v-if="selectedProductForModal">
				<!-- 헤더 -->
				<div class="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom flex-wrap">
					<div class="d-flex align-items-center">
						<img v-if="selectedProductForModal.image" :src="cdnImage(selectedProductForModal.image)"
							class="rounded mr-3" style="width:80px;height:80px;object-fit:cover;">
						<div>
							<h5 class="mb-1">{{ selectedProductForModal.itemName || ('상품 ' + selectedProductForModal.productId) }}</h5>
							<small class="text-muted">노출ID {{ selectedProductForModal.productId }}</small>
							<small class="text-muted ml-2">옵션 {{ modalProductOptions.length }}개</small>
							<small class="text-muted ml-2">키워드 {{ selectedProductForModal.keywords.length }}개</small>
						</div>
					</div>
					<b-button-group size="sm">
						<b-button variant="outline-primary" :disabled="isCollecting"
							@click="collectRankingsOnly(selectedProductForModal)"
							v-b-tooltip.hover title="이 상품의 키워드별 Top10 순위만 수집 (24h 캐시는 스킵)">
							<i class="ri-trophy-line mr-1"></i>이 상품 순위 수집
						</b-button>
						<b-button variant="outline-danger" :disabled="isCollecting"
							@click="clearProductData(selectedProductForModal)"
							v-b-tooltip.hover title="이 상품의 키워드/옵션 데이터 모두 삭제 (KeywordRanking 캐시는 보존)">
							<i class="ri-delete-bin-line mr-1"></i>이 상품 데이터 초기화
						</b-button>
					</b-button-group>
				</div>

				<!-- 심층 경쟁분석 (chip + 차트1 시계열 + 슬라이더 + 차트2 분포) -->
				<div class="card mb-3">
					<div class="card-body py-2">
						<div class="d-flex justify-content-between align-items-start mb-2 flex-wrap">
							<small class="text-muted">
								<b>심층 경쟁분석</b> — 상품별 30일 노출 시계열 + 시점 분포 (★ 빨강 = 내 상품, 굵은 컬러 = Top5, 회색 = 기타)
							</small>
							<small class="text-muted">
								⚙ 설정 [심층 경쟁분석] ON 후 [수집]을 진행해야 데이터가 채워집니다.
							</small>
						</div>

						<!-- chip 키워드 필터 -->
						<div class="mb-2" style="font-size:85%;">
							<b-button-group size="sm">
								<b-button :variant="competitorChipKeyword === 'all' ? 'primary' : 'outline-secondary'"
									@click="competitorChipKeyword = 'all'">전체</b-button>
								<b-button v-for="kw in modalCompetitorChipKeywords" :key="kw"
									:variant="competitorChipKeyword === kw ? 'primary' : 'outline-secondary'"
									@click="competitorChipKeyword = kw">{{ kw }}</b-button>
							</b-button-group>
						</div>

						<!-- 차트1 Y축 토글 (노출수 ↔ 순위) -->
						<div class="d-flex justify-content-end mb-1" style="font-size:80%;">
							<b-button-group size="sm">
								<b-button :variant="competitorYAxisMode === 'impression' ? 'primary' : 'outline-secondary'"
									@click="competitorYAxisMode = 'impression'" v-b-tooltip.hover title="세로축 = 노출수 절대값">
									<i class="ri-bar-chart-2-line mr-1"></i>노출수
								</b-button>
								<b-button :variant="competitorYAxisMode === 'rank' ? 'primary' : 'outline-secondary'"
									@click="competitorYAxisMode = 'rank'" v-b-tooltip.hover title="세로축 = 그 시점 노출수 순위 (1위가 위)">
									<i class="ri-trophy-line mr-1"></i>순위
								</b-button>
							</b-button-group>
						</div>

						<!-- 차트1: 상품별 30일 노출 시계열 -->
						<ApexChart v-if="competitorChart1Options" element="competitor-chart-1"
							:chartOption="competitorChart1Options" />
						<div v-else class="text-center py-3 text-muted" style="font-size:85%;">
							경쟁사 데이터 없음. ⚙ 설정 [심층 경쟁분석] ON 후 [수집]을 다시 진행하세요.
						</div>

						<!-- 슬라이더 (좌측=오래됨, 우측=최신) + 눈금/날짜 라벨 + thumb 위 현재 시점 -->
						<div class="mt-3 mb-1 d-flex align-items-center" style="font-size:85%;">
							<span class="mr-2 text-muted" style="white-space:nowrap;">시점:</span>
							<div class="flex-grow-1 competitor-slider-wrap">
								<div class="slider-current-label" :style="{ left: sliderProgressPct }">
									{{ competitorSliderDate || '-' }}
								</div>
								<input type="range" min="0" :max="sliderDates.length - 1"
									v-model.number="competitorSliderIndex"
									class="competitor-range d-block w-100" />
								<div class="competitor-slider-ticks d-flex justify-content-between">
									<span v-for="(d, i) in sliderDates" :key="i"
										class="tick" :class="{ 'tick-major': i % 5 === 0 || i === sliderDates.length - 1 }">
										<span class="tick-bar"></span>
										<span v-if="i % 5 === 0 || i === sliderDates.length - 1" class="tick-label">
											{{ d.slice(5).replace('-', '/') }}
										</span>
									</span>
								</div>
							</div>
						</div>

						<!-- 차트2: 슬라이더 시점 분포 -->
						<ApexChart v-if="competitorChart2Options" element="competitor-chart-2"
							:chartOption="competitorChart2Options" />
					</div>
				</div>

				<!-- 차트 0: 키워드별 노출수 추이 -->
				<div class="card mb-3">
					<div class="card-body py-2">
						<small class="text-muted d-block mb-1">
							<b>키워드별 노출수 추이 (30일)</b>
						</small>
						<ApexChart v-if="modalKeywordImpressionChartOptions" element="prod-modal-keyword-imp-chart"
							:chartOption="modalKeywordImpressionChartOptions" />
						<div v-else class="text-center py-3 text-muted" style="font-size:85%;">
							키워드별 일별 노출 데이터가 없습니다.
						</div>
					</div>
				</div>

				<!-- 차트 1: 노출/클릭/판매 -->
				<div class="card mb-3">
					<div class="card-body py-2">
						<small class="text-muted d-block mb-1">
							<b>노출 / 클릭 / 판매 추이 (30일, 모든 옵션 합산)</b>
						</small>
						<ApexChart v-if="modalChart1Options" element="prod-modal-chart-1" :chartOption="modalChart1Options" />
					</div>
				</div>
				<!-- 차트 2: CTR / CVR -->
				<div class="card mb-3">
					<div class="card-body py-2">
						<small class="text-muted d-block mb-1">
							<b>CTR (키워드 클릭/노출) / CVR (총판매/총클릭) — %</b>
						</small>
						<ApexChart v-if="modalChart2Options" element="prod-modal-chart-2" :chartOption="modalChart2Options" />
					</div>
				</div>

				<!-- 옵션별 데이터 (메인 테이블과 동일 구조) -->
				<div v-if="modalProductOptions.length > 0">
					<div class="d-flex justify-content-between align-items-center mb-2">
						<h6 class="mb-0">옵션별 일자별 데이터</h6>
						<small class="text-muted">메인 테이블과 동일한 컬럼 구조 · 옵션마다 1행</small>
					</div>
					<CDataTable
						storage-key="exposureTracking.2"
						:headers="tableHeaders"
						:items="modalProductOptions"
						:height="'500px'"
						:show-select="false"
						:item-key="'rowKey'"
						:fixed-columns="'rankChanges'">
						<!-- 순위 (옵션 단위) -->
						<template v-slot:rank="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-for="(kw, kIdx) in item.keywords" :key="kIdx"
									class="kw-row text-center" style="font-size:80%;">
									<template v-if="getKeywordRankInfo(item, kw.keyword)">
										<span v-if="getMyRank(item, kw.keyword)" class="badge bg-primary"
											v-b-tooltip.hover.html :title="rankTooltip(item, kw.keyword).replace(/\n/g, '<br>')">
											{{ getMyRank(item, kw.keyword) }}위
										</span>
										<span v-else class="badge bg-secondary">외</span>
									</template>
									<span v-else class="text-muted">-</span>
								</div>
							</template>
							<div class="kw-row stat-row text-center">-</div>
							<div class="kw-row stat-row text-center">-</div>
						</template>

						<!-- 옵션 정보 (썸네일/액션 X) -->
						<template v-slot:productInfo="{ item }">
							<div style="min-width:200px;">
								<div style="font-size:90%;line-height:130%;font-weight:600;">
									{{ item.itemName }}
								</div>
								<small class="text-muted d-block" v-if="item.lastFetchedAt">
									최근수집: {{ formatDateTime(item.lastFetchedAt) }}
								</small>
							</div>
						</template>

						<!-- 키워드 -->
						<template v-slot:keywords="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-if="item.keywords.length === 0" class="text-muted text-center py-2"
									style="font-size:85%;">데이터 없음</div>
								<div v-for="(kw, kIdx) in item.keywords" :key="kIdx" class="kw-row"
									style="font-size:85%;" :title="kw.keyword">
									<span v-if="kw.isNew" class="badge bg-warning mr-1">🆕</span>
									<span v-else-if="kw.keywordType === 'tracked'" class="badge bg-info mr-1">★</span>
									{{ kw.keyword }}
								</div>
							</template>
							<div class="kw-row stat-row" style="font-size:85%;">
								<span class="badge bg-primary">클릭</span>
							</div>
							<div class="kw-row stat-row" style="font-size:85%;">
								<span class="badge bg-success">판매</span>
							</div>
						</template>

						<!-- 변화 -->
						<template v-slot:rankChanges="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-for="(kw, kIdx) in item.keywords" :key="kIdx"
									class="kw-row text-right" style="font-size:80%;">
									<span class="metric-inline">
										<span class="m-imp" :class="changeColor(kw.change.impression)">{{ formatChange(kw.change.impression, 'impression') }}</span>
										<span class="m-clk" :class="changeColor(kw.change.srpClick)">{{ formatChange(kw.change.srpClick, 'srpClick') }}</span>
										<span class="m-ctr" :class="changeColor(kw.change.ctr)">{{ formatChange(kw.change.ctr, 'ctr') }}</span>
									</span>
								</div>
							</template>
							<div class="kw-row stat-row text-right" style="font-size:80%;"
								:class="changeColor(getProductStatChange(item, 'click'))">
								{{ formatChange(getProductStatChange(item, 'click'), 'click') }}
							</div>
							<div class="kw-row stat-row text-right" style="font-size:80%;"
								:class="changeColor(getProductStatChange(item, 'sale'))">
								{{ formatChange(getProductStatChange(item, 'sale'), 'sale') }}
							</div>
						</template>

						<!-- 날짜 -->
						<template v-for="(date, dIdx) in dates" v-slot:[`date_${date}`]="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-for="(kw, kIdx) in item.keywords"
									:key="`${item.rowKey}-${kIdx}-${dIdx}`"
									class="kw-row text-right" style="font-size:80%;">
									<span class="metric-inline">
										<span class="m-imp">{{ getCellMulti(kw, datesinit[dIdx]).imp }}</span>
										<span class="m-clk">{{ getCellMulti(kw, datesinit[dIdx]).clk }}</span>
										<span class="m-ctr">{{ getCellMulti(kw, datesinit[dIdx]).ctr }}</span>
									</span>
								</div>
							</template>
							<div :key="`stat-click-${item.rowKey}-${dIdx}`"
								class="kw-row stat-row text-right">
								{{ getProductStat(item, datesinit[dIdx], 'click') }}
							</div>
							<div :key="`stat-sale-${item.rowKey}-${dIdx}`"
								class="kw-row stat-row text-right">
								{{ getProductStat(item, datesinit[dIdx], 'sale') }}
							</div>
						</template>

						<!-- 차트 -->
						<template v-slot:chartBtn="{ item }">
							<template v-if="viewMode === 'detail'">
								<div v-for="(kw, kIdx) in item.keywords" :key="kIdx" class="kw-row text-center"
									style="font-size:85%;">
									<a href="javascript:void(0)" v-if="kw.dailyDataLength > 0"
										@click="openChart(item, kw)">
										<i class="ri-line-chart-line"></i>
									</a>
									<span v-else class="text-muted">-</span>
								</div>
							</template>
							<div class="kw-row stat-row text-center">-</div>
							<div class="kw-row stat-row text-center">-</div>
						</template>
					</CDataTable>
				</div>
				<div v-else class="text-center text-muted py-3">
					옵션별 데이터가 없습니다. [수집]을 한 번 진행해주세요.
				</div>
			</div>
		</b-modal>
	</div>
</template>

<script>
import axios from 'axios'
import CDataTable from '@/components/common/CDataTable'
import ApexChart from '@/components/charts/ApexChart.vue'

const NEW_BADGE_DAYS = 7
const TABLE_DAYS = 30

export default {
	name: 'ExposureTracking',
	components: { CDataTable, ApexChart },
	data() {
		return {
			loading: true,
			activeTabIndex: 0,
			growthSales: [],
			growthDB: [],
			dailySalesData: [],   // /api/growth/getSalesLedger 응답 (일별 판매/조회)
			keywordRankings: [], // /api/analysis/getKeywordRankings 응답 (키워드별 Top 10 캐시)
			exposureItems: [],
			competitors: [],     // /api/analysis/getCompetitors — 경쟁상품 등록 레지스트리
			competitorUrl: '',
			isCollecting: false,
			progressMessage: '',
			pendingResolve: null,
			pendingTimeout: null,
			tableSelected: [],
			viewMode: 'detail',
			topN: 20,
			selectedProductForModal: null,  // 모달에 표시할 상품 group
			collectRanking: false,  // 키워드 Top10 순위 같이 수집할지 (느림)
			chartTitle: '',
			chartOptions: null,
			// 순서 저장
			savedOrder: { productOrder: {}, keywordOrder: {} },
			isOrderEditMode: false,
			draftProductOrder: [],          // [productId, ...] 편집 중 상품 순서
			draftKeywordOrder: {},          // { productId: [keyword, ...] } 편집 중 키워드 순서
			selectedProductForOrder: null,  // productId
			selectedKeywordForOrder: null,  // { productId, keyword }
			// 심층 경쟁분석 (모달 안)
			competitorChipKeyword: 'all',   // 'all' 또는 키워드 문자열
			competitorSliderIndex: 29,      // 0=30일전, 29=어제 (좌측 오래됨, 우측 최신)
			competitorYAxisMode: 'impression'  // 'impression' (노출수) | 'rank' (노출수 순위)
		}
	},
	computed: {
		// 테이블 표시순: 어제 → 30일 전 (오늘은 데이터 없음, 제외)
		datesinit() {
			const arr = []
			const today = new Date()
			for (let i = 1; i <= TABLE_DAYS; i++) {
				const d = new Date(today.getTime() - i * 86400000)
				arr.push(d.toISOString().split('T')[0])
			}
			return arr
		},
		dates() {
			const WEEK = ['일', '월', '화', '수', '목', '금', '토']
			return this.datesinit.map(iso => {
				const d = new Date(iso)
				return `${iso.slice(5)} (${WEEK[d.getDay()]})`
			})
		},
		tableHeaders() {
			const base = [
				{ text: '상품정보', value: 'productInfo', isSlot: true, align: 'left', width: 280 },
				{ text: '키워드', value: 'keywords', isSlot: true, align: 'left', width: 140 },
				{ text: '어제순위', value: 'rank', isSlot: true, align: 'center', width: 80 },
				{ text: '변화\n(노출/클릭/CTR)', value: 'rankChanges', isSlot: true, align: 'right', width: 150 }
			]
			const dateHeaders = this.dates.map(date => ({
				text: date, value: `date_${date}`, isSlot: true, align: 'right', width: 140
			}))
			const tail = [
				{ text: '차트', value: 'chartBtn', isSlot: true, align: 'center', width: 50 }
			]
			return [...base, ...dateHeaders, ...tail]
		},
		growthByProductId() {
			const map = {}
			this.growthDB.forEach(g => {
				if (!g.productId) return
				if (!map[g.productId]) {
					map[g.productId] = {
						itemName: g.displayProductName || g.itemName || g.productName || '',
						image: g.imagePath || g.imgUrl || '',
						vendorItemId: g.vendorItemId || '',
						itemId: g.itemId ? String(g.itemId) : '',
						leafCategoryCode: g.displayCategoryCode ? String(g.displayCategoryCode) : ''
					}
				}
			})
			return map
		},
		// 경쟁상품 등록 레지스트리 — productId → 메타 (데이터 초기화돼도 유지됨)
		competitorMetaById() {
			const map = {}
			this.competitors.forEach(c => {
				if (!c || !c.productId) return
				map[String(c.productId)] = {
					itemName: c.productName || '',
					image: c.imagePath || '',
					vendorItemId: c.vendorItemId || '',
					itemId: c.itemId ? String(c.itemId) : '',
					leafCategoryCode: c.leafCategoryCode ? String(c.leafCategoryCode) : ''
				}
			})
			return map
		},
		// 합산 row만 (vendorItemId='' 또는 missing) — productId 행에 표시되는 데이터
		// ownership='auto'는 심층경쟁분석 전용이라 메인 테이블에서 제외 (모달에서만 사용)
		exposureByProductId() {
			const map = {}
			this.exposureItems.forEach(exp => {
				if (exp.ownership === 'auto') return
				const vid = exp.vendorItemId || ''
				if (vid !== '') return
				if (!map[exp.productId]) map[exp.productId] = []
				map[exp.productId].push(exp)
			})
			return map
		},
		// 옵션별 row만 — { productId: { vendorItemId: [exp...] } }
		// 키워드 순위 캐시 lookup: { 'keyword|leafCategoryCode': KeywordRanking doc }
		rankByKeyCat() {
			const map = {}
			this.keywordRankings.forEach(r => {
				if (!r || !r.keyword || !r.leafCategoryCode) return
				map[r.keyword + '|' + String(r.leafCategoryCode)] = r
			})
			return map
		},
		exposureOptionsByProductId() {
			const map = {}
			this.exposureItems.forEach(exp => {
				if (exp.ownership === 'auto') return
				const vid = exp.vendorItemId || ''
				if (vid === '') return
				if (!map[exp.productId]) map[exp.productId] = {}
				if (!map[exp.productId][vid]) map[exp.productId][vid] = []
				map[exp.productId][vid].push(exp)
			})
			return map
		},
		// 상품별 총 판매량 (정렬용)
		totalSalesByProductId() {
			const map = {}
			this.growthSales.forEach(g => {
				if (!g.productId) return
				const sales = Array.isArray(g.salesHistory) ? g.salesHistory : []
				const sum = sales.reduce((s, v) => s + (Number(v) || 0), 0)
				map[String(g.productId)] = sum
			})
			return map
		},
		// vendorItemId → productId 매핑 (dailySalesData 합산용)
		vendorItemToProductId() {
			const map = {}
			this.growthSales.forEach(g => {
				if (g.vendorItemId && g.productId) map[String(g.vendorItemId)] = String(g.productId)
			})
			this.growthDB.forEach(g => {
				if (g.vendorItemId && g.productId) map[String(g.vendorItemId)] = String(g.productId)
			})
			return map
		},
		// 상품별 일별 트래픽 (PDP 클릭/판매) — dailySalesData에서 직접 합산 (ranking.vue 패턴)
		// { [productId]: { [isoDate]: { click, sale } } }
		trafficByProductId() {
			const map = {}
			const vidToPid = this.vendorItemToProductId
			this.dailySalesData.forEach(d => {
				if (!d || !d.vendorItemId || !d.date) return
				const pid = vidToPid[String(d.vendorItemId)]
				if (!pid) return
				if (!map[pid]) map[pid] = {}
				if (!map[pid][d.date]) map[pid][d.date] = { click: 0, sale: 0 }
				map[pid][d.date].click += Number(d.traffic && d.traffic.pageViews) || 0
				map[pid][d.date].sale += Number(d.sales && d.sales.unitsSold) || 0
			})
			return map
		},
		// 상품별 일별 트래픽 (vendorItemId 단위) — 옵션 row용
		trafficByVendorItem() {
			const map = {}
			this.dailySalesData.forEach(d => {
				if (!d || !d.vendorItemId || !d.date) return
				const vid = String(d.vendorItemId)
				if (!map[vid]) map[vid] = {}
				if (!map[vid][d.date]) map[vid][d.date] = { click: 0, sale: 0 }
				map[vid][d.date].click += Number(d.traffic && d.traffic.pageViews) || 0
				map[vid][d.date].sale += Number(d.sales && d.sales.unitsSold) || 0
			})
			return map
		},
		// 상품(productId) 단위 7일 판매량 합 — GrowthDB의 vendorItemId rows 합산
		unitsSoldLast7ByProductId() {
			const map = {}
			this.growthDB.forEach(g => {
				if (!g.productId) return
				const v = Number(g.unitsSoldForLast7Days) || 0
				map[g.productId] = (map[g.productId] || 0) + v
			})
			return map
		},
		// 내 상품 전체 (slice 적용 전)
		mineProductsAll() {
			const seen = {}
			const list = []
			this.growthSales.forEach(g => {
				if (!g.productId) return
				const pid = String(g.productId)
				if (seen[pid]) return
				seen[pid] = true
				list.push(this._buildGroup(pid, 'mine', g))
			})
			Object.keys(this.exposureByProductId).forEach(pid => {
				if (seen[pid]) return
				const exps = this.exposureByProductId[pid]
				const isMine = exps.some(e => (e.ownership || 'mine') === 'mine')
				if (isMine) { seen[pid] = true; list.push(this._buildGroup(pid, 'mine', null)) }
			})
			return this._applyProductOrder(list)
		},
		mineProducts() {
			const sorted = this.mineProductsAll
			const n = parseInt(this.topN, 10)
			return (n > 0 && this.activeTabIndex === 0) ? sorted.slice(0, n) : sorted
		},
		competitorProducts() {
			// 레지스트리 등록분 + 기존 데이터(ownership:competitor) 합집합.
			// → 데이터 초기화돼도 등록만 있으면 탭에 계속 표시됨.
			const pidSet = {}
			Object.keys(this.competitorMetaById).forEach(pid => { pidSet[pid] = true })
			Object.keys(this.exposureByProductId).forEach(pid => {
				const exps = this.exposureByProductId[pid]
				if (exps.some(e => e.ownership === 'competitor')) pidSet[pid] = true
			})
			const list = Object.keys(pidSet).map(pid => this._buildGroup(pid, 'competitor', null))
			return this._applyProductOrder(list)
		},
		currentProducts() {
			return this.activeTabIndex === 0 ? this.mineProducts : this.competitorProducts
		},
		selectedProductCount() {
			return this.tableSelected.filter(i => !i.isOption).length
		},
		// CDataTable items: productId 합산 row만 (옵션은 모달로)
		tableItems() {
			return this.currentProducts.map(g => {
				const optMap = this.exposureOptionsByProductId[g.productId] || {}
				return { ...g, rowKey: g.productId, isOption: false, optionCount: Object.keys(optMap).length }
			})
		},
		// 모달용: 선택된 상품의 옵션 그룹 리스트
		modalProductOptions() {
			if (!this.selectedProductForModal) return []
			const pid = this.selectedProductForModal.productId
			const optMap = this.exposureOptionsByProductId[pid] || {}
			return this._buildOptionGroups(this.selectedProductForModal, optMap)
		},
		// 모달 차트 0: 키워드별 일별 노출수 (30일) — 키워드 하나당 라인 1개
		modalKeywordImpressionChartOptions() {
			if (!this.selectedProductForModal) return null
			const keywords = this.selectedProductForModal.keywords || []
			if (keywords.length === 0) return null
			const dates = this.datesinit.slice().reverse()  // 오래된 → 최신
			// 키워드 중복 제거 + 옵션 합산 (같은 키워드가 여러 row로 들어와 있을 수 있음)
			const sumByKw = {}
			keywords.forEach(kw => {
				const name = kw.keyword
				if (!name) return
				if (!sumByKw[name]) {
					sumByKw[name] = {}
					dates.forEach(d => { sumByKw[name][d] = 0 })
				}
				const dd = Array.isArray(kw.dailyData) ? kw.dailyData : []
				dd.forEach(d => {
					if (d && d.date && sumByKw[name][d.date] !== undefined) {
						sumByKw[name][d.date] += Number(d.impression) || 0
					}
				})
			})
			// 전체 노출 0인 키워드는 차트에서 제외 (라인 너무 많아지는 것 방지)
			const series = Object.keys(sumByKw)
				.map(name => ({ name, type: 'line', data: dates.map(d => sumByKw[name][d]) }))
				.filter(s => s.data.some(v => v > 0))
				.sort((a, b) => {
					const sumB = b.data.reduce((s, v) => s + v, 0)
					const sumA = a.data.reduce((s, v) => s + v, 0)
					return sumB - sumA
				})
			if (series.length === 0) return null
			return {
				series,
				chart: { type: 'line', height: 320, zoom: { enabled: true }, toolbar: { show: false }, animations: { enabled: false } },
				stroke: { width: 2, curve: 'smooth' },
				xaxis: { categories: dates, type: 'category', labels: { rotate: -45, style: { fontSize: '10px' } } },
				yaxis: [{ labels: { formatter: v => (v || 0).toLocaleString('ko-KR') } }],
				dataLabels: { enabled: false },
				legend: { position: 'top', fontSize: '11px', itemMargin: { horizontal: 6, vertical: 2 } },
				tooltip: { shared: true, intersect: false }
			}
		},
		// 모달 차트 1: 노출/클릭/판매 절대값 (30일)
		modalChart1Options() {
			if (!this.selectedProductForModal) return null
			const pid = this.selectedProductForModal.productId
			const dates = this.datesinit.slice().reverse()  // 오래된 → 최신
			const sumByDate = {}
			dates.forEach(d => { sumByDate[d] = { totalImp: 0, organicImp: 0, adImp: 0, srpClick: 0 } })
			// 옵션별 row만 합산 (vendorItemId !== '') — 합산 row 중복 방지
			this.exposureItems.forEach(exp => {
				if (String(exp.productId) !== String(pid)) return
				if (!exp.vendorItemId) return
				if (!Array.isArray(exp.dailyData)) return
				exp.dailyData.forEach(d => {
					if (sumByDate[d.date]) {
						sumByDate[d.date].totalImp += Number(d.impression) || 0
						sumByDate[d.date].organicImp += Number(d.organicImpression) || 0
						sumByDate[d.date].adImp += Number(d.adImpression) || 0
						sumByDate[d.date].srpClick += Number(d.srpClick) || 0
					}
				})
			})
			const traffic = this.trafficByProductId[pid] || {}
			const series = [
				{ name: '키워드 자연노출', type: 'line', data: dates.map(d => sumByDate[d].organicImp) },
				{ name: '키워드 광고노출', type: 'line', data: dates.map(d => sumByDate[d].adImp) },
				{ name: '키워드 클릭', type: 'line', data: dates.map(d => sumByDate[d].srpClick) },
				{ name: '총 클릭', type: 'line', data: dates.map(d => Number(traffic[d] && traffic[d].click) || 0) },
				{ name: '총 판매', type: 'line', data: dates.map(d => Number(traffic[d] && traffic[d].sale) || 0) }
			]
			return {
				series,
				chart: { type: 'line', height: 280, zoom: { enabled: true }, toolbar: { show: false } },
				stroke: { width: 2, curve: 'smooth' },
				colors: ['#198754', '#dc3545', '#0dcaf0', '#0d6efd', '#fd7e14'],
				xaxis: { categories: dates, type: 'category', labels: { rotate: -45, style: { fontSize: '10px' } } },
				yaxis: [{ labels: { formatter: v => (v || 0).toLocaleString('ko-KR') } }],
				dataLabels: { enabled: false },
				legend: { position: 'top' }
			}
		},
		// 모달 순위 차트: 키워드별 line + 내 위치 marker(빨강 큰 점)
		// X축: 1~10위 + "순위 밖" (11번째)
		rankChartOptions() {
			if (!this.selectedProductForModal) return null
			const lcc = this.selectedProductForModal.leafCategoryCode
			if (!lcc) return null
			const keywords = this.selectedProductForModal.keywords || []
			if (keywords.length === 0) return null

			const ranks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
			const categories = ranks.map(r => r + '위').concat(['순위 밖'])
			const series = []
			const discreteMarkers = []
			const offRankPoints = []
			const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]
			const myRankByKw = {}  // { keyword: 1~10 or 11 }

			const productGroup = this.selectedProductForModal
			keywords.forEach(kw => {
				const info = this.rankByKeyCat[kw.keyword + '|' + String(lcc)]
				if (!info || !Array.isArray(info.top10) || info.top10.length === 0) return
				const data = ranks.map(r => {
					const found = info.top10.find(t => t.rank === r)
					return found ? Number(found.impression) || 0 : null
				})
				data.push(null)
				const seriesIdx = series.length
				series.push({ name: kw.keyword, type: 'line', data: data })

				// 내 옵션 / 경쟁상품 본인 itemId 매칭
				const mine = info.top10.find(t => this.isMyItem(productGroup, t))
				if (mine) {
					myRankByKw[kw.keyword] = mine.rank
					discreteMarkers.push({
						seriesIndex: seriesIdx,
						dataPointIndex: mine.rank - 1,
						size: 12,
						fillColor: '#dc3545',
						strokeColor: '#fff',
						strokeWidth: 3
					})
				} else {
					const dd = (kw.dailyData || []).find(d => d.date === yesterday)
					const myImp = dd ? Number(dd.impression) || 0 : 0
					myRankByKw[kw.keyword] = 11
					offRankPoints.push({ x: '순위 밖', y: myImp, kw: kw.keyword })
				}
			})

			if (series.length === 0) return null
			const lineCount = series.length

			// 순위 밖 키워드가 있으면 별도 scatter series (빨강)
			const ownLabel = (this.selectedProductForModal && this.selectedProductForModal.ownership === 'competitor') ? '이 상품' : '내 상품'
			if (offRankPoints.length > 0) {
				series.push({
					name: '★ ' + ownLabel + ' (순위 밖)',
					type: 'scatter',
					data: offRankPoints,
					color: '#dc3545'
				})
			}

			const lineColors = ['#0d6efd', '#198754', '#6f42c1', '#fd7e14', '#20c997', '#0dcaf0', '#d63384', '#ffc107', '#6c757d', '#adb5bd']
			const colors = []
			for (let i = 0; i < lineCount; i++) colors.push(lineColors[i % lineColors.length])
			if (offRankPoints.length > 0) colors.push('#dc3545')

			return {
				series: series,
				chart: { type: 'line', height: 360, toolbar: { show: false }, zoom: { enabled: false } },
				stroke: {
					width: offRankPoints.length > 0
						? [...Array(lineCount).fill(2), 0]
						: 2,
					curve: 'smooth'
				},
				markers: {
					size: offRankPoints.length > 0
						? [...Array(lineCount).fill(4), 14]
						: 4,
					discrete: discreteMarkers,
					strokeColors: '#fff',
					strokeWidth: 2,
					hover: { sizeOffset: 2 }
				},
				colors: colors,
				xaxis: {
					type: 'category',
					categories: categories,
					title: { text: '순위', style: { fontSize: '11px' } },
					labels: { style: { fontSize: '10px' } }
				},
				yaxis: {
					title: { text: '노출수 (어제 기준, log scale)', style: { fontSize: '11px' } },
					logarithmic: true,
					logBase: 10,
					min: 1,
					labels: { formatter: v => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : String(Math.round(v || 0)) }
				},
				legend: { position: 'bottom', fontSize: '11px', markers: { width: 10, height: 10 } },
				dataLabels: { enabled: false },
				tooltip: {
					shared: true,
					intersect: false,
					custom: function ({ series, dataPointIndex, w }) {
						const names = w.globals.seriesNames || []
						const colorList = w.globals.colors || []
						const rankLabel = categories[dataPointIndex] || ''
						let html = '<div style="padding:8px 10px;font-size:12px;background:#fff;">'
						html += '<div style="font-weight:600;margin-bottom:6px;border-bottom:1px solid #eee;padding-bottom:4px;">' + rankLabel + '</div>'
						let shownAny = false
						names.forEach((name, i) => {
							const val = series[i] && series[i][dataPointIndex]
							const isScatter = name && name.indexOf('순위 밖') >= 0
							if (val == null && !isScatter) return
							const myRank = myRankByKw[name]
							const isMineHere = (myRank === (dataPointIndex + 1)) || (isScatter && dataPointIndex === 10)
							const valStr = val != null ? Number(val).toLocaleString('ko-KR') + ' 노출' : '-'
							const color = colorList[i] || '#666'
							html += '<div style="display:flex;align-items:center;gap:6px;line-height:1.5;">'
							html += '<span style="display:inline-block;width:9px;height:9px;background:' + color + ';border-radius:50%;flex:0 0 auto;"></span>'
							html += '<span>' + name + ': <b>' + valStr + '</b>'
							if (isMineHere) html += ' <span style="color:#dc3545;font-weight:bold;margin-left:4px;">★ ' + ownLabel + ' 위치</span>'
							html += '</span></div>'
							shownAny = true
						})
						if (!shownAny) html += '<div class="text-muted">데이터 없음</div>'
						html += '</div>'
						return html
					}
				}
			}
		},
		// 모달 차트 2: CTR / CVR (비율)
		modalChart2Options() {
			if (!this.selectedProductForModal) return null
			const pid = this.selectedProductForModal.productId
			const dates = this.datesinit.slice().reverse()
			const sumByDate = {}
			dates.forEach(d => { sumByDate[d] = { imp: 0, srpClick: 0 } })
			this.exposureItems.forEach(exp => {
				if (String(exp.productId) !== String(pid)) return
				if (!exp.vendorItemId) return
				if (!Array.isArray(exp.dailyData)) return
				exp.dailyData.forEach(d => {
					if (sumByDate[d.date]) {
						sumByDate[d.date].imp += Number(d.impression) || 0
						sumByDate[d.date].srpClick += Number(d.srpClick) || 0
					}
				})
			})
			const traffic = this.trafficByProductId[pid] || {}
			const ctrData = dates.map(d => {
				const imp = sumByDate[d].imp, clk = sumByDate[d].srpClick
				return imp > 0 ? parseFloat((clk / imp * 100).toFixed(2)) : 0
			})
			const cvrData = dates.map(d => {
				const click = Number(traffic[d] && traffic[d].click) || 0
				const sale = Number(traffic[d] && traffic[d].sale) || 0
				return click > 0 ? parseFloat((sale / click * 100).toFixed(2)) : 0
			})
			const series = [
				{ name: '키워드 CTR (%)', type: 'line', data: ctrData },
				{ name: 'CVR (%) = 총판매/총클릭', type: 'line', data: cvrData }
			]
			return {
				series,
				chart: { type: 'line', height: 220, zoom: { enabled: false }, toolbar: { show: false } },
				stroke: { width: 2, curve: 'smooth' },
				colors: ['#6f42c1', '#198754'],
				xaxis: { categories: dates, type: 'category', labels: { rotate: -45, style: { fontSize: '10px' } } },
				yaxis: [{ labels: { formatter: v => v.toFixed(2) + '%' } }],
				dataLabels: { enabled: false },
				legend: { position: 'top' }
			}
		},
		// 상단 통합 차트 (현재 탭 합산 30일 노출/클릭)
		summaryChartOptions() {
			const sumByDate = {}
			this.datesinit.forEach(d => { sumByDate[d] = { imp: 0, clk: 0 } })
			const wantOwnership = this.activeTabIndex === 0 ? 'mine' : 'competitor'
			this.exposureItems.forEach(exp => {
				const own = exp.ownership || 'mine'
				if (own !== wantOwnership) return
				if (!Array.isArray(exp.dailyData)) return
				exp.dailyData.forEach(d => {
					if (sumByDate[d.date]) {
						sumByDate[d.date].imp += Number(d.impression) || 0
						sumByDate[d.date].clk += Number(d.srpClick) || 0
					}
				})
			})
			// 차트는 항상 오래된 → 최신 (왼쪽이 옛날)
			const chartDates = this.datesinit.slice().reverse()
			const series = [
				{ name: '총 노출', type: 'line', data: chartDates.map(d => sumByDate[d].imp) },
				{ name: '총 클릭', type: 'line', data: chartDates.map(d => sumByDate[d].clk) }
			]
			return {
				series,
				chart: { type: 'line', height: 240, zoom: { enabled: false }, toolbar: { show: false } },
				stroke: { width: 2, curve: 'smooth' },
				colors: ['#495057', '#0d6efd'],
				xaxis: { categories: chartDates, type: 'category', labels: { rotate: -45, style: { fontSize: '10px' } } },
				yaxis: [
					{ title: { text: '노출수' }, labels: { formatter: v => (v || 0).toLocaleString('ko-KR') } }
				],
				dataLabels: { enabled: false },
				legend: { position: 'top' }
			}
		},

		// ─── 심층 경쟁분석 (모달 안) ───
		// 모달 chip 목록 = 선택된 상품의 키워드 리스트
		modalCompetitorChipKeywords() {
			if (!this.selectedProductForModal) return []
			return (this.selectedProductForModal.keywords || [])
				.map(k => k.keyword)
				.filter(Boolean)
		},
		// 슬라이더용 날짜 배열 (좌측=가장 오래됨, 우측=어제)
		sliderDates() {
			return this.datesinit.slice().reverse()
		},
		// 슬라이더 인덱스가 가리키는 ISO 날짜
		competitorSliderDate() {
			const idx = Number(this.competitorSliderIndex) || 0
			return this.sliderDates[idx] || null
		},
		// thumb 위치 (현재 시점 라벨용) — 0~100%
		sliderProgressPct() {
			const len = this.sliderDates.length || 1
			const max = Math.max(1, len - 1)
			const idx = Number(this.competitorSliderIndex) || 0
			return (idx / max * 100) + '%'
		},
		// 내 상품의 키워드별 노출 매트릭스: { keyword: { __mine__: { date: row } } }
		// (selectedProductForModal.keywords[].dailyData 기반)
		modalMineMatrix() {
			const product = this.selectedProductForModal
			if (!product) return {}
			const matrix = {}
			;(product.keywords || []).forEach(kw => {
				if (!kw || !kw.keyword) return
				matrix[kw.keyword] = matrix[kw.keyword] || {}
				matrix[kw.keyword].__mine__ = {}
				;(kw.dailyData || []).forEach(d => {
					if (!d || !d.date) return
					matrix[kw.keyword].__mine__[d.date] = d
				})
			})
			return matrix
		},
		// auto 데이터의 itemId별 메타: { itemId: { productName, imagePath } }
		modalAutoItemMeta() {
			const map = {}
			this.exposureItems.forEach(exp => {
				if (exp.ownership !== 'auto') return
				const iid = String(exp.itemId || exp.productId || '')
				if (!iid) return
				if (!map[iid] || (!map[iid].productName && exp.productName)) {
					map[iid] = { productName: exp.productName || '', imagePath: exp.imagePath || '' }
				}
			})
			return map
		},
		// auto 데이터 매트릭스: { keyword: { itemId: { date: row } } }
		// 선택된 상품의 키워드/lcc와 매칭되는 ownership='auto' 항목만
		modalAutoMatrix() {
			const product = this.selectedProductForModal
			if (!product) return {}
			const lcc = String(product.leafCategoryCode || '')
			const kwSet = new Set((product.keywords || []).map(k => k.keyword))
			const matrix = {}
			this.exposureItems.forEach(exp => {
				if (exp.ownership !== 'auto') return
				if (lcc && String(exp.leafCategoryCode || '') !== lcc) return
				if (!kwSet.has(exp.keyword)) return
				const iid = String(exp.itemId || exp.productId || '')
				if (!iid) return
				if (!matrix[exp.keyword]) matrix[exp.keyword] = {}
				if (!matrix[exp.keyword][iid]) matrix[exp.keyword][iid] = {}
				;(exp.dailyData || []).forEach(d => {
					if (!d || !d.date) return
					matrix[exp.keyword][iid][d.date] = d
				})
			})
			return matrix
		},
		// 차트1: 상품별 multi-line 노출 시계열 (가로=날짜, 세로=노출수)
		// chip='all' → 키워드 합산, chip=특정 → 그 키워드만
		competitorChart1Options() {
			const dates = this.datesinit.slice().reverse()  // 오래된 → 최신
			const chipKw = this.competitorChipKeyword
			const mine = this.modalMineMatrix
			const auto = this.modalAutoMatrix
			// perItem: { iid: { isMine, byDate: {date: imp} } }
			const perItem = {}
			const addMatrix = (matrix, isMine) => {
				Object.keys(matrix).forEach(kw => {
					if (chipKw !== 'all' && kw !== chipKw) return
					Object.keys(matrix[kw]).forEach(iid => {
						if (!perItem[iid]) perItem[iid] = { isMine, byDate: {} }
						Object.keys(matrix[kw][iid]).forEach(date => {
							const imp = Number(matrix[kw][iid][date].impression) || 0
							perItem[iid].byDate[date] = (perItem[iid].byDate[date] || 0) + imp
						})
					})
				})
			}
			addMatrix(mine, true)
			addMatrix(auto, false)
			const items = Object.keys(perItem).map(iid => ({
				iid,
				isMine: perItem[iid].isMine,
				total: Object.values(perItem[iid].byDate).reduce((s, v) => s + v, 0),
				byDate: perItem[iid].byDate
			}))
			if (items.length === 0) return null
			const mines = items.filter(i => i.isMine)
			// 가독성: 30일 총 노출 desc 상위 10개만 표시 (+ 내 상품은 무조건 별도)
			const others = items.filter(i => !i.isMine).sort((a, b) => b.total - a.total).slice(0, 10)
			const mode = this.competitorYAxisMode || 'impression'

			// 순위 모드: 각 날짜별로 노출 desc 정렬 → itemId별 그날 순위 매트릭스 생성
			// 노출 0인 항목은 순위 부여하지만 동률은 정렬 안정성에 의존
			let rankMatrix = null
			if (mode === 'rank') {
				rankMatrix = {}  // { iid: { date: rank } }
				dates.forEach(date => {
					const sorted = items
						.map(it => ({ iid: it.iid, imp: it.byDate[date] || 0 }))
						.sort((a, b) => b.imp - a.imp)
					sorted.forEach((s, idx) => {
						if (!rankMatrix[s.iid]) rankMatrix[s.iid] = {}
						rankMatrix[s.iid][date] = idx + 1
					})
				})
			}
			const valueAt = (it, d) => {
				if (mode === 'rank') {
					return (rankMatrix[it.iid] && rankMatrix[it.iid][d]) || null
				}
				return it.byDate[d] || 0
			}

			const series = []
			const colors = []
			const strokeWidths = []
			const top5Colors = ['#0d6efd', '#198754', '#fd7e14', '#6f42c1', '#0dcaf0']
			const itemMetaMap = this.modalAutoItemMeta
			const shortName = (n, len) => {
				if (!n) return ''
				return n.length > len ? n.slice(0, len) + '…' : n
			}
			mines.forEach(m => {
				series.push({ name: '★ 내 상품', data: dates.map(d => valueAt(m, d)) })
				colors.push('#dc3545')
				strokeWidths.push(3)
			})
			others.forEach((o, idx) => {
				const isTop5 = idx < 5
				const meta = itemMetaMap[o.iid] || {}
				const pname = shortName(meta.productName || '', 30)
				const fallback = isTop5 ? `Top ${idx + 1}` : `#${idx + 1}`
				const label = pname ? `${isTop5 ? 'Top ' + (idx + 1) + ' · ' : ''}${pname}` : fallback
				series.push({ name: label, data: dates.map(d => valueAt(o, d)) })
				colors.push(isTop5 ? top5Colors[idx] : '#dee2e6')
				strokeWidths.push(isTop5 ? 2 : 1)
			})

			// Y축 설정 (mode별)
			const yaxis = mode === 'rank'
				? [{
					title: { text: '순위 (1위 = 위)', style: { fontSize: '11px' } },
					reversed: true,
					min: 1,
					max: items.length,
					tickAmount: Math.max(1, items.length - 1),
					labels: { formatter: v => Math.round(v) + '위' }
				}]
				: [{
					title: { text: '노출수' },
					labels: { formatter: v => (v || 0).toLocaleString('ko-KR') }
				}]
			return {
				series,
				chart: { type: 'line', height: 600, zoom: { enabled: false }, toolbar: { show: false }, animations: { enabled: false } },
				stroke: { width: strokeWidths, curve: mode === 'rank' ? 'straight' : 'smooth' },
				colors: colors,
				xaxis: { categories: dates, type: 'category', labels: { rotate: -45, style: { fontSize: '10px' } } },
				yaxis: yaxis,
				dataLabels: { enabled: false },
				legend: { position: 'top', fontSize: '11px' }
			}
		},
		// 차트2: 슬라이더 시점에 상품별 노출 line (내림차순), 내 상품 마커 강조
		competitorChart2Options() {
			const chipKw = this.competitorChipKeyword
			const sliderDate = this.competitorSliderDate
			if (!sliderDate) return null
			const mine = this.modalMineMatrix
			const auto = this.modalAutoMatrix
			const itemMap = {}  // iid → { isMine, imp }
			const collect = (matrix, isMine) => {
				Object.keys(matrix).forEach(kw => {
					if (chipKw !== 'all' && kw !== chipKw) return
					Object.keys(matrix[kw]).forEach(iid => {
						const d = matrix[kw][iid][sliderDate]
						const imp = d ? Number(d.impression) || 0 : 0
						if (!itemMap[iid]) itemMap[iid] = { isMine, imp: 0 }
						itemMap[iid].imp += imp
					})
				})
			}
			collect(mine, true)
			collect(auto, false)
			const items = Object.keys(itemMap).map(iid => ({ iid, ...itemMap[iid] }))
			if (items.length === 0) return null
			items.sort((a, b) => b.imp - a.imp)
			const categories = items.map((_it, idx) => '#' + (idx + 1))
			const discreteMarkers = []
			items.forEach((it, idx) => {
				if (it.isMine) {
					discreteMarkers.push({
						seriesIndex: 0,
						dataPointIndex: idx,
						size: 12,
						fillColor: '#dc3545',
						strokeColor: '#fff',
						strokeWidth: 3
					})
				}
			})
			return {
				series: [{ name: '노출수', data: items.map(it => it.imp) }],
				chart: { type: 'line', height: 280, zoom: { enabled: false }, toolbar: { show: false }, animations: { enabled: false } },
				stroke: { width: 2, curve: 'straight' },
				colors: ['#6c757d'],
				markers: { size: 4, discrete: discreteMarkers, strokeColors: '#fff', strokeWidth: 2 },
				xaxis: {
					type: 'category',
					categories: categories,
					title: { text: '순번 (노출수 내림차순)', style: { fontSize: '11px' } },
					labels: { style: { fontSize: '10px' } }
				},
				yaxis: [{
					title: { text: '노출수', style: { fontSize: '11px' } },
					labels: { formatter: v => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : String(Math.round(v || 0)) }
				}],
				dataLabels: { enabled: false },
				legend: { show: false },
				tooltip: {
					custom: (function (metaMap) {
						return function ({ dataPointIndex }) {
							const it = items[dataPointIndex]
							if (!it) return ''
							const meta = metaMap[it.iid] || {}
							const pname = meta.productName || ''
							const label = it.isMine
								? '★ 내 상품'
								: (pname || ('itemId ...' + String(it.iid).slice(-6)))
							return '<div style="padding:6px 10px;font-size:12px;background:#fff;max-width:320px;white-space:normal;">'
								+ '<div style="font-weight:600;">' + label + '</div>'
								+ '<div>노출 ' + (it.imp || 0).toLocaleString('ko-KR') + '</div>'
								+ '</div>'
						}
					})(this.modalAutoItemMeta)
				}
			}
		}
	},
	mounted() {
		window.addEventListener('message', this.handleMessage)
		// 설정 복원 (localStorage)
		try {
			const v = localStorage.getItem('exposureTracking.collectRanking')
			if (v === 'true') this.collectRanking = true
		} catch (e) { /* ignore */ }
		this.loadAll()
	},
	watch: {
		topN(newVal) {
			if (typeof newVal !== 'number' || newVal <= 0) return
			const total = this.mineProductsAll.length
			if (total > 0 && newVal > total) {
				this.$nextTick(() => { this.topN = total })
			}
		},
		collectRanking(newVal) {
			try { localStorage.setItem('exposureTracking.collectRanking', String(!!newVal)) } catch (e) { /* ignore */ }
		},
	},
	beforeDestroy() {
		window.removeEventListener('message', this.handleMessage)
		this.clearPendingTimeout()
	},
	methods: {
		formatNum(n) { return (Number(n) || 0).toLocaleString('ko-KR') },
		// 쿠팡 CDN 풀 URL 조립 (path만이면 prefix 추가, 이미 http면 그대로)
		cdnImage(path) {
			if (!path) return ''
			if (/^https?:\/\//i.test(path)) return path
			return 'http://thumbnail.coupangcdn.com/thumbnails/remote/300x300ex/image/' + path
		},
		// Top 10 entry가 이 상품의 옵션인지 판정 (isBelongToVendor 또는 itemId 매칭)
		isMyItem(group, t) {
			if (!t) return false
			if (t.isMine) return true  // Wing API의 isBelongToVendor (mine 자동)
			if (!group || !group.myItemIds || !t.itemId) return false
			return group.myItemIds.indexOf(String(t.itemId)) >= 0
		},
		formatK(n) {
			const v = Number(n) || 0
			if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M'
			if (v >= 1000) return (v / 1000).toFixed(1) + 'k'
			return String(v)
		},
		formatChange(v, type) {
			if (v === null || v === undefined || v === 0) return '-'
			const sign = v > 0 ? '▲ ' : '▼ '
			const abs = Math.abs(v)
			if (type === 'ctr') return sign + abs.toFixed(2) + '%'
			return sign + abs.toLocaleString('ko-KR')
		},
		changeColor(v) {
			if (v > 0) return 'text-primary'
			if (v < 0) return 'text-secondary'
			return ''
		},
		formatDateTime(d) {
			if (!d) return ''
			const dt = new Date(d)
			const pad = n => String(n).padStart(2, '0')
			return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`
		},
		generateDatesFromUpdate(updateDate) {
			if (!updateDate) return []
			const baseDate = new Date(updateDate)
			const dates = []
			for (let i = 29; i >= 0; i--) {
				const d = new Date(baseDate)
				d.setDate(baseDate.getDate() - i)
				dates.push(d.toISOString().split('T')[0])
			}
			return dates
		},
		getProductStat(item, isoDate, key) {
			let m
			if (item && item.isOption) {
				m = this.trafficByVendorItem[item.vendorItemId]
			} else {
				const pid = item && item.productId !== undefined ? item.productId : item
				m = this.trafficByProductId[pid]
			}
			if (!m || !m[isoDate]) return '-'
			const v = Number(m[isoDate][key]) || 0
			return v === 0 ? '-' : this.formatNum(v)
		},
		// 상품/옵션 단위 어제 vs 오늘 변화량 (last - prev)
		getProductStatChange(item, key) {
			const m = item.isOption
				? this.trafficByVendorItem[item.vendorItemId]
				: this.trafficByProductId[item.productId]
			if (!m) return null
			const dates = Object.keys(m).sort()
			if (dates.length < 2) return null
			const last = m[dates[dates.length - 1]] || {}
			const prev = m[dates[dates.length - 2]] || {}
			return (Number(last[key]) || 0) - (Number(prev[key]) || 0)
		},
		// 키워드별 Top 10 캐시 lookup
		getKeywordRankInfo(item, keyword) {
			const lcc = item && item.leafCategoryCode
			if (!lcc || !keyword) return null
			return this.rankByKeyCat[keyword + '|' + String(lcc)] || null
		},
		// 내(또는 경쟁상품 본인) 옵션 중 Top 10에 있는 가장 높은 순위
		getMyRank(item, keyword) {
			const info = this.getKeywordRankInfo(item, keyword)
			if (!info || !Array.isArray(info.top10)) return null
			const mine = info.top10.filter(t => this.isMyItem(item, t))
			if (mine.length === 0) return null
			return Math.min.apply(null, mine.map(t => t.rank))
		},
		rankTooltip(item, keyword) {
			const info = this.getKeywordRankInfo(item, keyword)
			if (!info || !info.stats) return '순위 데이터 없음'
			const my = this.getMyRank(item, keyword)
			const label = (item && item.ownership === 'competitor') ? '이 상품' : '내 상품'
			const myStr = my ? `${label} 순위: ${my}위 / 10` : `${label}: 순위 밖 (Top 10 외)`
			const s = info.stats
			return `${myStr}\nTop 10 노출 (최근 7일):\n  max ${(s.max || 0).toLocaleString('ko-KR')}\n  avg ${(s.avg || 0).toLocaleString('ko-KR')}\n  min ${(s.min || 0).toLocaleString('ko-KR')}`
		},
		getCellMulti(kw, isoDate) {
			const d = kw.dailyByDate[isoDate]
			if (!d) return { imp: '-', clk: '-', ctr: '-' }
			const imp = Number(d.impression) || 0
			const clk = Number(d.srpClick) || 0
			return {
				imp: imp === 0 ? '-' : this.formatNum(imp),
				clk: clk === 0 ? '-' : this.formatNum(clk),
				ctr: imp > 0 ? ((clk / imp) * 100).toFixed(2) + '%' : '-'
			}
		},

		// === 정렬 helpers ===
		// productOrder 적용 (편집모드면 draft, 아니면 savedOrder, 둘 다 없으면 판매량 desc)
		_applyProductOrder(list) {
			if (this.isOrderEditMode && this.draftProductOrder.length > 0) {
				const map = {}
				this.draftProductOrder.forEach((pid, i) => { map[pid] = i })
				return list.slice().sort((a, b) => {
					const ai = map[a.productId], bi = map[b.productId]
					if (ai !== undefined && bi !== undefined) return ai - bi
					if (ai !== undefined) return -1
					if (bi !== undefined) return 1
					return 0
				})
			}
			const order = this.savedOrder.productOrder || {}
			return list.slice().sort((a, b) => {
				const ao = order[a.productId], bo = order[b.productId]
				if (ao !== undefined && bo !== undefined) return ao - bo
				if (ao !== undefined) return -1
				if (bo !== undefined) return 1
				// 7일 판매량 desc 우선, 동일하면 30일 판매량 desc
				const a7 = this.unitsSoldLast7ByProductId[a.productId] || 0
				const b7 = this.unitsSoldLast7ByProductId[b.productId] || 0
				if (a7 !== b7) return b7 - a7
				const as = this.totalSalesByProductId[a.productId] || 0
				const bs = this.totalSalesByProductId[b.productId] || 0
				return bs - as
			})
		},
		_applyKeywordOrder(pid, keywords) {
			if (this.isOrderEditMode && this.draftKeywordOrder[pid]) {
				const map = {}
				this.draftKeywordOrder[pid].forEach((kw, i) => { map[kw] = i })
				return keywords.slice().sort((a, b) => {
					const ai = map[a.keyword], bi = map[b.keyword]
					if (ai !== undefined && bi !== undefined) return ai - bi
					if (ai !== undefined) return -1
					if (bi !== undefined) return 1
					return 0
				})
			}
			const order = this.savedOrder.keywordOrder || {}
			return keywords.slice().sort((a, b) => {
				const ak = pid + '|' + a.keyword
				const bk = pid + '|' + b.keyword
				const ao = order[ak], bo = order[bk]
				if (ao !== undefined && bo !== undefined) return ao - bo
				if (ao !== undefined) return -1
				if (bo !== undefined) return 1
				const av = a.dailyData.length > 0 ? Number(a.dailyData[a.dailyData.length - 1].impression) || 0 : 0
				const bv = b.dailyData.length > 0 ? Number(b.dailyData[b.dailyData.length - 1].impression) || 0 : 0
				return bv - av
			})
		},

		// 옵션 row 빌드 (펼친 상품의 vendorItemId별 그룹)
		_buildOptionGroups(productGroup, optMap) {
			const cutoff = Date.now() - NEW_BADGE_DAYS * 24 * 60 * 60 * 1000
			const vids = Object.keys(optMap)
			const gdbByVid = {}
			this.growthDB.forEach(g => {
				if (String(g.productId) !== String(productGroup.productId)) return
				gdbByVid[String(g.vendorItemId)] = g
			})
			return vids.map(vid => {
				const exps = optMap[vid] || []
				const gdbRow = gdbByVid[vid]
				let lastFetchedAt = null
				exps.forEach(e => {
					if (e.lastFetchedAt) {
						const ts = new Date(e.lastFetchedAt).getTime()
						if (!lastFetchedAt || ts > new Date(lastFetchedAt).getTime()) lastFetchedAt = e.lastFetchedAt
					}
				})
				const rawKeywords = exps.map(e => {
					const dailyData = Array.isArray(e.dailyData) ? e.dailyData : []
					const dailyByDate = {}
					dailyData.forEach(d => { if (d && d.date) dailyByDate[d.date] = d })
					const sorted = dailyData.slice().sort((a, b) => new Date(a.date) - new Date(b.date))
					const last = sorted[sorted.length - 1]
					const prev = sorted[sorted.length - 2]
					const change = { impression: null, srpClick: null, ctr: null }
					if (last && prev) {
						change.impression = (Number(last.impression) || 0) - (Number(prev.impression) || 0)
						change.srpClick = (Number(last.srpClick) || 0) - (Number(prev.srpClick) || 0)
						const lc = (Number(last.impression) || 0) > 0 ? (Number(last.srpClick) || 0) / (Number(last.impression) || 0) * 100 : 0
						const pc = (Number(prev.impression) || 0) > 0 ? (Number(prev.srpClick) || 0) / (Number(prev.impression) || 0) * 100 : 0
						change.ctr = parseFloat((lc - pc).toFixed(2))
					}
					const isNew = e.firstSeenDate && (new Date(e.firstSeenDate).getTime() >= cutoff)
					return {
						keyword: e.keyword,
						keywordType: e.keywordType || 'discovered',
						firstSeenDate: e.firstSeenDate || '',
						isNew, dailyByDate,
						dailyDataLength: dailyData.length, dailyData, change
					}
				})
				const keywords = this._applyKeywordOrder(productGroup.productId, rawKeywords)
				const units7d = gdbRow ? (Number(gdbRow.unitsSoldForLast7Days) || 0) : 0
				const optItemId = gdbRow && gdbRow.itemId ? String(gdbRow.itemId) : (exps[0] && exps[0].itemId ? String(exps[0].itemId) : '')
				return {
					rowKey: `${productGroup.productId}-${vid}`,
					productId: productGroup.productId,
					vendorItemId: vid,
					ownership: productGroup.ownership,
					isOption: true,
					itemName: '└ 옵션 ' + vid + (units7d > 0 ? ` (7일판매 ${units7d})` : ''),
					image: '',
					itemId: optItemId,
					leafCategoryCode: productGroup.leafCategoryCode,
					lastFetchedAt, keywords,
					myItemIds: optItemId ? [optItemId] : []
				}
			})
		},

		_buildGroup(pid, ownership, growthRow) {
			const exps = this.exposureByProductId[pid] || []
			const gdb = this.growthByProductId[pid] || {}
			let itemName = gdb.itemName, image = gdb.image, vendorItemId = gdb.vendorItemId
			let itemId = gdb.itemId || '', leafCategoryCode = gdb.leafCategoryCode || ''
			let lastFetchedAt = null

			exps.forEach(e => {
				if (!itemName && e.productName) itemName = e.productName
				if (!image && e.imagePath) image = e.imagePath
				if (!leafCategoryCode && e.leafCategoryCode) leafCategoryCode = e.leafCategoryCode
				if (!itemId && e.itemId) itemId = e.itemId
				if (!vendorItemId && e.vendorItemId) vendorItemId = e.vendorItemId
				if (e.lastFetchedAt) {
					const ts = new Date(e.lastFetchedAt).getTime()
					if (!lastFetchedAt || ts > new Date(lastFetchedAt).getTime()) lastFetchedAt = e.lastFetchedAt
				}
			})
			if (!vendorItemId && growthRow && growthRow.vendorItemId) vendorItemId = growthRow.vendorItemId
			// 경쟁상품: growthDB/수집데이터에 없으면 레지스트리 메타로 보강
			// (데이터 초기화 후에도 이름/이미지가 유지되도록)
			if (ownership === 'competitor') {
				const cmeta = this.competitorMetaById[pid]
				if (cmeta) {
					if (!itemName) itemName = cmeta.itemName
					if (!image) image = cmeta.image
					if (!vendorItemId) vendorItemId = cmeta.vendorItemId
					if (!itemId) itemId = cmeta.itemId
					if (!leafCategoryCode) leafCategoryCode = cmeta.leafCategoryCode
				}
			}

			const cutoff = Date.now() - NEW_BADGE_DAYS * 24 * 60 * 60 * 1000
			const rawKeywords = exps.map(e => {
				const dailyData = Array.isArray(e.dailyData) ? e.dailyData : []
				const dailyByDate = {}
				dailyData.forEach(d => { if (d && d.date) dailyByDate[d.date] = d })
				const sorted = dailyData.slice().sort((a, b) => new Date(a.date) - new Date(b.date))
				const last = sorted[sorted.length - 1]
				const prev = sorted[sorted.length - 2]
				const change = { impression: null, srpClick: null, ctr: null }
				if (last && prev) {
					change.impression = (Number(last.impression) || 0) - (Number(prev.impression) || 0)
					change.srpClick = (Number(last.srpClick) || 0) - (Number(prev.srpClick) || 0)
					const lastCtr = (Number(last.impression) || 0) > 0 ? (Number(last.srpClick) || 0) / (Number(last.impression) || 0) * 100 : 0
					const prevCtr = (Number(prev.impression) || 0) > 0 ? (Number(prev.srpClick) || 0) / (Number(prev.impression) || 0) * 100 : 0
					change.ctr = parseFloat((lastCtr - prevCtr).toFixed(2))
				}
				const isNew = e.firstSeenDate && (new Date(e.firstSeenDate).getTime() >= cutoff)
				return {
					keyword: e.keyword,
					keywordType: e.keywordType || 'discovered',
					firstSeenDate: e.firstSeenDate || '',
					isNew, dailyByDate,
					dailyDataLength: dailyData.length, dailyData, change
				}
			})
			const keywords = this._applyKeywordOrder(pid, rawKeywords)

			// myItemIds: 이 상품의 모든 옵션 itemId (mine: GrowthDB row들, competitor: ExposureTracking row들)
			const myItemIds = []
			this.growthDB.forEach(g => {
				if (String(g.productId) === String(pid) && g.itemId) {
					const v = String(g.itemId)
					if (myItemIds.indexOf(v) < 0) myItemIds.push(v)
				}
			})
			exps.forEach(e => {
				if (e.itemId) {
					const v = String(e.itemId)
					if (myItemIds.indexOf(v) < 0) myItemIds.push(v)
				}
			})

			return {
				productId: pid, ownership,
				itemName, image, vendorItemId, itemId, leafCategoryCode,
				lastFetchedAt, keywords, myItemIds
			}
		},

		async loadAll() {
			this.loading = true
			try {
				const companyid = this.$store.state.user && this.$store.state.user.companyid

				// 1. growthSales / growthdb는 store 캐시 우선 (ranking.vue 패턴)
				const storeSales = this.$store.state.growthSales
				const storeGdb = this.$store.state.growthdb
				const fetches = []
				if (storeSales && storeSales.length > 0) {
					this.growthSales = storeSales
				} else {
					fetches.push(axios.post('/api/getGrowthSalesV2').then(r => {
						this.growthSales = (r.data && Array.isArray(r.data.growthSales)) ? r.data.growthSales : []
						if (this.growthSales.length > 0) this.$store.commit('growthSalesUpdate', this.growthSales)
					}))
				}
				if (storeGdb && storeGdb.length > 0) {
					this.growthDB = storeGdb
				} else {
					// show 없이 전량 — 좁히면 판매장부가 물려받아 숨김 상품 매출이 사라진다(#407).
					// 여기선 productId 별 옵션/메타 룩업이라 전량이어도 표시 행이 늘지 않는다.
					fetches.push(axios.post('/api/getGrowthDBV2').then(r => {
						this.growthDB = (r.data && Array.isArray(r.data.growthdb)) ? r.data.growthdb : []
						if (this.growthDB.length > 0) this.$store.commit('growthdbupdate', this.growthDB)
					}))
				}

				// 2. 나머지는 항상 fetch (store 없음)
				const sortedDates = this.datesinit.slice().sort()
				const startDate = sortedDates[0]
				const endDate = sortedDates[sortedDates.length - 1]
				fetches.push(
					axios.post('/api/analysis/getExposureTrace', { companyid }).then(r => {
						this.exposureItems = Array.isArray(r.data) ? r.data : []
					}),
					axios.post('/api/analysis/getExposureOrder', { companyid }).then(r => {
						this.savedOrder = {
							productOrder: (r.data && r.data.productOrder) || {},
							keywordOrder: (r.data && r.data.keywordOrder) || {}
						}
					}),
					axios.post('/api/growth/getSalesLedger', { startDate, endDate }).then(r => {
						this.dailySalesData = (r.data && r.data.res === 'success' && Array.isArray(r.data.dailySalesData))
							? r.data.dailySalesData : []
					}),
					axios.post('/api/analysis/getKeywordRankings', { companyid }).then(r => {
						this.keywordRankings = Array.isArray(r.data) ? r.data : []
					}),
					axios.post('/api/analysis/getCompetitors', { companyid }).then(r => {
						this.competitors = Array.isArray(r.data) ? r.data : []
					})
				)
				await Promise.all(fetches)
			} catch (e) {
				console.error('노출추적 로드 실패:', e)
			} finally {
				this.loading = false
			}
		},
		onChangeTab(idx) {
			this.activeTabIndex = idx
			this.tableSelected = []
		},

		// === 순서 편집 모드 ===
		enterOrderEditMode() {
			this.isOrderEditMode = true
			this.tableSelected = []
			this.selectedProductForOrder = null
			this.selectedKeywordForOrder = null
			this.draftProductOrder = this.currentProducts.map(p => p.productId)
			this.draftKeywordOrder = {}
			this.currentProducts.forEach(p => {
				this.draftKeywordOrder[p.productId] = p.keywords.map(k => k.keyword)
			})
		},
		cancelOrder() {
			this.isOrderEditMode = false
			this.draftProductOrder = []
			this.draftKeywordOrder = {}
			this.selectedProductForOrder = null
			this.selectedKeywordForOrder = null
		},
		async saveOrder() {
			// draft → savedOrder 갱신 (현재 탭의 productId만 갱신, 다른 탭 순서는 유지)
			const newProductOrder = { ...(this.savedOrder.productOrder || {}) }
			this.draftProductOrder.forEach((pid, i) => { newProductOrder[pid] = i })
			const newKeywordOrder = { ...(this.savedOrder.keywordOrder || {}) }
			Object.keys(this.draftKeywordOrder).forEach(pid => {
				this.draftKeywordOrder[pid].forEach((kw, i) => {
					newKeywordOrder[pid + '|' + kw] = i
				})
			})
			try {
				await axios.post('/api/analysis/saveExposureOrder', {
					productOrder: newProductOrder,
					keywordOrder: newKeywordOrder
				})
				this.savedOrder = { productOrder: newProductOrder, keywordOrder: newKeywordOrder }
				this.isOrderEditMode = false
				this.draftProductOrder = []
				this.draftKeywordOrder = {}
				this.selectedProductForOrder = null
				this.selectedKeywordForOrder = null
			} catch (e) {
				console.error('순서 저장 실패:', e)
				alert('순서 저장 실패')
			}
		},
		selectProductForOrder(pid) {
			this.selectedProductForOrder = pid
			this.selectedKeywordForOrder = null
		},
		selectKeywordForOrder(pid, kw) {
			this.selectedKeywordForOrder = { productId: pid, keyword: kw }
			this.selectedProductForOrder = null
		},
		isKeywordSelected(pid, kw) {
			return this.selectedKeywordForOrder
				&& this.selectedKeywordForOrder.productId === pid
				&& this.selectedKeywordForOrder.keyword === kw
		},
		moveSelected(dir) {
			if (this.selectedProductForOrder) {
				this._moveInArray(this.draftProductOrder, this.selectedProductForOrder, dir)
			} else if (this.selectedKeywordForOrder) {
				const pid = this.selectedKeywordForOrder.productId
				const arr = this.draftKeywordOrder[pid] || []
				this._moveInArray(arr, this.selectedKeywordForOrder.keyword, dir)
				this.$set(this.draftKeywordOrder, pid, arr)
			}
		},
		_moveInArray(arr, item, dir) {
			const idx = arr.indexOf(item)
			if (idx < 0) return
			arr.splice(idx, 1)
			let newIdx = idx
			if (dir === 'top') newIdx = 0
			else if (dir === 'bottom') newIdx = arr.length
			else if (dir === 'up') newIdx = Math.max(0, idx - 1)
			else if (dir === 'down') newIdx = Math.min(arr.length, idx + 1)
			arr.splice(newIdx, 0, item)
		},

		// === Extension 통신 ===
		handleMessage(event) {
			if (!event || !event.data) return
			const d = event.data
			if (d.res === 'productTraceProgress') {
				this.progressMessage = `${d.message || ''} (${d.progress || 0}%)`
			} else if (d.res === 'productTraceComplete') {
				this._resolvePending({ kind: 'daily', data: d })
			} else if (d.res === 'productTraceError') {
				this._resolvePending({ kind: 'daily', error: d.message || '데일리 수집 실패' })
			} else if (d.res === 'productTrafficKeywordsComplete') {
				this._resolvePending({ kind: 'discover', data: d })
			} else if (d.res === 'productTrafficKeywordsError') {
				this._resolvePending({ kind: 'discover', error: d.message || '키워드 발견 실패' })
			} else if (d.type === 'addKeywordToTraceComplete') {
				this._resolvePending({ kind: 'addKw', data: d })
			} else if (d.res === 'getKeywordRankItemsResult') {
				this._resolvePending({ kind: 'rankItems', data: d })
			} else if (d.res === 'getKeywordItemsDateWiseResult') {
				this._resolvePending({ kind: 'dateWiseItems', data: d })
			} else if (d.res === 'getItemWinnerResult') {
				this._resolvePending({ kind: 'itemWinner', data: d })
			}
		},
		_resolvePending(payload) {
			if (this.pendingResolve) {
				const r = this.pendingResolve
				this.pendingResolve = null
				this.clearPendingTimeout()
				r(payload)
			}
		},
		clearPendingTimeout() {
			if (this.pendingTimeout) { clearTimeout(this.pendingTimeout); this.pendingTimeout = null }
		},
		_waitForExtension(timeoutMs) {
			// 이전 pending이 있으면 안전하게 종료 (시리얼 호출 보장)
			this.clearPendingTimeout()
			if (this.pendingResolve) {
				const prev = this.pendingResolve
				this.pendingResolve = null
				try { prev({ error: '새 호출로 인해 종료' }) } catch (e) { /* ignore */ }
			}
			return new Promise(resolve => {
				this.pendingResolve = resolve
				this.pendingTimeout = setTimeout(() => {
					if (this.pendingResolve) {
						const r = this.pendingResolve
						this.pendingResolve = null
						r({ error: '시간 초과' })
					}
				}, timeoutMs || 5 * 60 * 1000)
			})
		},

		async _runDiscover(item) {
			this.progressMessage = `[${item.itemName || item.productId}] 키워드 발견(7일) 중...`
			const productLink = `https://www.coupang.com/vp/products/${item.productId}?vendorItemId=${item.vendorItemId || ''}`
			const wait = this._waitForExtension()
			// 내 상품은 growthDB에서 온 itemId/leafCategoryCode를 그대로 전달 →
			// extension이 Wing pre-matching으로 재해석하지 않고 스킵 (비보유 상품만 fallback)
			window.postMessage({
				greeting: 'getProductTrafficKeywords',
				productLink: productLink, days: [7], limit: 20,
				itemId: item.itemId || '',
				leafCategoryCode: item.leafCategoryCode || '',
				productName: item.itemName || '',
				imagePath: item.image || ''
			}, '*')
			const result = await wait
			if (result.error) return { ok: false, error: result.error }
			const info = (result.data && result.data.productInfo) || {}
			const traffic = Array.isArray(info.trafficKeyword) ? info.trafficKeyword : []
			const keywords = traffic.map(t => ({
				keyword: t.keywordDetail && t.keywordDetail.keyword,
				impression: (t.keywordMetric && t.keywordMetric.impression) || 0,
				organicImpression: (t.keywordMetric && t.keywordMetric.organicImpression) || 0,
				adImpression: (t.keywordMetric && t.keywordMetric.adImpression) || 0,
				srpClick: (t.keywordMetric && t.keywordMetric.srpClick) || 0,
				searchVolume: (t.keywordMetric && t.keywordMetric.searchVolume) || 0
			})).filter(k => k.keyword)
			if (keywords.length === 0) return { ok: true, info, count: 0, keywordList: [] }
			await axios.post('/api/analysis/saveDiscoveredKeywords', {
				productId: item.productId, ownership: item.ownership,
				productName: info.productName || item.itemName || '',
				imagePath: info.imagePath || info.imgUrl || item.image || '',
				itemId: info.itemId || item.itemId || '',
				leafCategoryCode: info.leafCategoryCode || item.leafCategoryCode || '',
				keywords: keywords
			})
			return { ok: true, info, count: keywords.length, keywordList: keywords.map(k => k.keyword) }
		},

		// 데일리 raw (저장하지 않고 trafficData만 반환) — 옵션 합산용
		// keywordList 인자: 7일 발견 키워드 리스트. 있으면 background.js가 Wing Top 단계 건너뛰고 이 키워드들로 직접 호출
		async _runDailyRaw(item, keywordList) {
			if (!item.vendorItemId) return { ok: false, error: 'vendorItemId 없음' }
			// 어제 ~ 30일 전 (오늘은 데이터 없음)
			const today = new Date()
			const startDate = new Date(today.getTime() - 30 * 86400000)
			const endDate = new Date(today.getTime() - 1 * 86400000)
			const startISO = startDate.toISOString().split('T')[0]
			const endISO = endDate.toISOString().split('T')[0]
			const wait = this._waitForExtension()
			window.postMessage({
				type: 'startProductTraceV2',
				productId: item.productId, vendorItemId: item.vendorItemId,
				itemId: item.itemId || null, leafCategoryCode: item.leafCategoryCode || null,
				productName: item.itemName || '', imagePath: item.image || '',
				keywords: Array.isArray(keywordList) && keywordList.length > 0 ? keywordList : null,
				dateRange: '30', startDate: startISO, endDate: endISO
			}, '*')
			const result = await wait
			if (result.error) return { ok: false, error: result.error }
			const productInfo = (result.data && result.data.productInfo) || {}
			const trafficData = Array.isArray(result.data && result.data.trafficData) ? result.data.trafficData : []
			return { ok: true, trafficData, productInfo }
		},
		// productId의 모든 옵션 (vendorItemId + itemId + leafCategoryCode) - 7일 판매량 desc
		_getProductOptions(productId) {
			return this.growthDB
				.filter(g => String(g.productId) === String(productId))
				.map(g => ({
					vendorItemId: g.vendorItemId || '',
					itemId: g.itemId ? String(g.itemId) : '',
					leafCategoryCode: g.displayCategoryCode ? String(g.displayCategoryCode) : '',
					units7d: Number(g.unitsSoldForLast7Days) || 0
				}))
				.filter(o => o.itemId && o.vendorItemId)
				.sort((a, b) => b.units7d - a.units7d)
		},

		// 키워드별 Top 10 순위 호출 + 백엔드 캐시 저장
		_isCacheFresh(rankDoc) {
			if (!rankDoc || !rankDoc.fetchedAt) return false
			const age = Date.now() - new Date(rankDoc.fetchedAt).getTime()
			return age < 24 * 60 * 60 * 1000
		},
		async _runKeywordRankItems(keyword, leafCategoryCode) {
			// Wing API가 1일짜리는 거부 → 7일 윈도우 (어제-6일 ~ 어제)
			const today = new Date()
			const startDate = new Date(today.getTime() - 7 * 86400000).toISOString().split('T')[0]
			const endDate = new Date(today.getTime() - 1 * 86400000).toISOString().split('T')[0]
			console.log('[순위 호출 시작]', keyword, 'lcc:', leafCategoryCode)
			const wait = this._waitForExtension(60 * 1000)
			window.postMessage({
				type: 'getKeywordRankItems',
				keyword: keyword,
				leafCategoryCode: String(leafCategoryCode),
				startDate: startDate,
				endDate: endDate
			}, '*')
			const result = await wait
			console.log('[순위 응답]', keyword, result)
			if (result.error) return { ok: false, error: result.error }
			const apiData = result.data && result.data.data
			if (apiData && apiData.__error) {
				console.error('[순위 호출 - API 오류]', keyword, apiData.__error, apiData.__body || '')
				return { ok: false, error: apiData.__error }
			}
			if (!Array.isArray(apiData)) return { ok: false, error: '응답 비어있음 또는 형식 오류' }
			const top10 = apiData.map(it => ({
				rank: it.keywordItemDetail && it.keywordItemDetail.rank,
				itemId: it.keywordItemDetail && it.keywordItemDetail.itemId,
				isMine: !!(it.keywordItemDetail && it.keywordItemDetail.isBelongToVendor),
				impression: (it.keywordMetric && it.keywordMetric.impression) || 0,
				srpClick: (it.keywordMetric && it.keywordMetric.srpClick) || 0,
				ctr: (it.keywordMetric && it.keywordMetric.ctr) || 0,
				avgPrice: it.keywordItemDetail && it.keywordItemDetail.avgPrice
			}))
			const imps = top10.map(x => x.impression).filter(v => v > 0)
			const stats = imps.length > 0 ? {
				max: Math.max(...imps),
				min: Math.min(...imps),
				avg: Math.round(imps.reduce((s, v) => s + v, 0) / imps.length)
			} : { max: 0, min: 0, avg: 0 }
			const searchVolume = (apiData[0] && apiData[0].keywordMetric && apiData[0].keywordMetric.searchVolume) || 0
			await axios.post('/api/analysis/saveKeywordRanking', {
				keyword: keyword,
				leafCategoryCode: String(leafCategoryCode),
				top10: top10,
				stats: stats,
				searchVolume: searchVolume
			})
			return { ok: true }
		},

		// 심층 경쟁분석: 키워드 × 다중 itemIds × 30일 노출/클릭 (Wing date-wise)
		// 응답: { "YYYY-MM-DD": [{ keywordItemDetail{itemId,isBelongToVendor,...}, keywordMetric{...} }, ...] }
		async _runKeywordItemsDateWise30(keyword, leafCategoryCode, itemIdsCsv) {
			const today = new Date()
			const startDate = new Date(today.getTime() - 30 * 86400000).toISOString().split('T')[0]
			const endDate = new Date(today.getTime() - 1 * 86400000).toISOString().split('T')[0]
			const wait = this._waitForExtension(2 * 60 * 1000)
			window.postMessage({
				type: 'getKeywordItemsDateWise',
				keyword: keyword,
				leafCategoryCode: String(leafCategoryCode),
				startDate: startDate,
				endDate: endDate,
				itemIdsCsv: itemIdsCsv
			}, '*')
			const result = await wait
			if (result.error) return { ok: false, error: result.error }
			const apiData = result.data && result.data.data
			if (apiData && apiData.__error) {
				console.error('[date-wise multi - API 오류]', keyword, apiData.__error)
				return { ok: false, error: apiData.__error }
			}
			if (!apiData || typeof apiData !== 'object') return { ok: false, error: '응답 비어있음 또는 형식 오류' }
			return { ok: true, data: apiData }
		},

		// 심층 경쟁분석: itemIds 다중 메타 (productName, imageUrl)
		// 응답: { "<itemId>": { itemName, productName, imageUrl, productId, vendorItemId, ... } }
		async _runItemWinner(itemIds, startDate, endDate, refKeyword, refLcc) {
			if (!Array.isArray(itemIds) || itemIds.length === 0) return { ok: false, error: 'itemIds 없음' }
			const wait = this._waitForExtension(2 * 60 * 1000)
			window.postMessage({
				type: 'getItemWinner',
				itemIds: itemIds.map(String),
				startDate: startDate,
				endDate: endDate,
				refKeyword: refKeyword || '',
				refLcc: refLcc || ''
			}, '*')
			const result = await wait
			if (result.error) return { ok: false, error: result.error }
			const apiData = result.data && result.data.data
			if (apiData && apiData.__error) {
				console.error('[item-winner - API 오류]', apiData.__error, 'status:', apiData.__status, '본문 일부:', apiData.__body || '')
				return { ok: false, error: apiData.__error }
			}
			if (!apiData || typeof apiData !== 'object') return { ok: false, error: '응답 비어있음 또는 형식 오류' }
			return { ok: true, data: apiData }
		},

		// 단일 키워드 데일리 (수동 추가용 - addKeywordToTrace 호출)
		async _runAddKeywordDaily(item, keyword) {
			if (!item.leafCategoryCode || !item.itemId) return { ok: false, error: 'category/itemId 없음 (다음 [수집] 후 가능)' }
			this.progressMessage = `[${keyword}] 데일리 수집 중...`
			const today = new Date()
			const startDate = new Date(today.getTime() - 29 * 86400000)
			const startISO = startDate.toISOString().split('T')[0]
			const endISO = today.toISOString().split('T')[0]
			const wait = this._waitForExtension()
			window.postMessage({
				type: 'addKeywordToTrace',
				keyword: keyword,
				leafCategoryCode: item.leafCategoryCode,
				itemIds: item.itemId,
				startDate: startISO, endDate: endISO
			}, '*')
			const result = await wait
			if (result.error) return { ok: false, error: result.error }
			const kData = result.data && result.data.keywordData
			if (!kData) return { ok: false, error: '응답 비어있음' }
			const dailyData = Array.isArray(kData.dailyData) ? kData.dailyData : []
			await axios.post('/api/analysis/saveExposureTrace', {
				productId: item.productId, keyword: keyword,
				productName: item.itemName || '', imagePath: item.image || '',
				itemId: item.itemId || '', leafCategoryCode: item.leafCategoryCode || '',
				ownership: item.ownership, keywordType: 'tracked', dailyData: dailyData
			})
			return { ok: true }
		},

		// 통합 수집: 7일 키워드 발견 + 모든 옵션 데일리 수집 + 키워드별 합산 저장
		// withRanking: 키워드별 Top10 순위도 같이 수집할지 (느림). undefined면 this.collectRanking
		async collectOne(item, withRanking) {
			const doRanking = (withRanking === undefined) ? this.collectRanking : !!withRanking
			if (this.isCollecting) return { ok: false, error: '수집 중' }
			this.isCollecting = true
			try {
				// 1. 키워드 발견 (한 번만)
				const r1 = await this._runDiscover(item)
				if (!r1.ok) { this.progressMessage = `발견 오류: ${r1.error}`; return r1 }

				// 2. 옵션 목록 (GrowthDB에서) - 7일 판매량 desc
				let options = this._getProductOptions(item.productId)
				if (options.length === 0) {
					// GrowthDB에 없으면 fallback (현재 item or 발견에서 받은 값)
					const fb = {
						vendorItemId: item.vendorItemId || (r1.info && r1.info.vendorItemId) || '',
						itemId: (r1.info && r1.info.itemId) || item.itemId || '',
						leafCategoryCode: (r1.info && r1.info.leafCategoryCode) || item.leafCategoryCode || '',
						units7d: 0
					}
					if (!fb.vendorItemId || !fb.itemId) {
						this.progressMessage = '옵션 정보(itemId/vendorItemId)를 찾을 수 없습니다.'
						return { ok: false, error: 'no options' }
					}
					options = [fb]
				}

				// 7일 발견 키워드 리스트 — background.js에 직접 전달해서 그 키워드들의 dailyData만 받음
				const targetKeywords = r1.keywordList || []

				// 3. 옵션마다 _runDailyRaw → 옵션별 보관 + 합산
				const merged = {}            // 합산: { keyword: { date: {...} } }
				const byOption = {}          // 옵션별: { vendorItemId: { keyword: { date: {...} } } }
				let okOptions = 0
				for (let i = 0; i < options.length; i++) {
					const opt = options[i]
					this.progressMessage = `[${item.itemName || item.productId}] 옵션 ${i + 1}/${options.length} 수집 중...`
					const fresh = {
						productId: item.productId,
						vendorItemId: opt.vendorItemId,
						itemId: opt.itemId,
						leafCategoryCode: opt.leafCategoryCode || (r1.info && r1.info.leafCategoryCode) || item.leafCategoryCode || '',
						itemName: (r1.info && r1.info.productName) || item.itemName,
						image: (r1.info && (r1.info.imagePath || r1.info.imgUrl)) || item.image,
						ownership: item.ownership
					}
					const r = await this._runDailyRaw(fresh, targetKeywords)
					if (!r.ok) continue
					okOptions++
					if (!byOption[opt.vendorItemId]) byOption[opt.vendorItemId] = {}
					r.trafficData.forEach(td => {
						const kw = td.keywordDetail && td.keywordDetail.keyword
						if (!kw) return
						if (!merged[kw]) merged[kw] = {}
						if (!byOption[opt.vendorItemId][kw]) byOption[opt.vendorItemId][kw] = {}
						const dailyData = Array.isArray(td.dailyData) ? td.dailyData : []
						dailyData.forEach(d => {
							if (!d || !d.date) return
							// 옵션별 보관 (raw)
							byOption[opt.vendorItemId][kw][d.date] = {
								date: d.date,
								impression: Number(d.impression) || 0,
								organicImpression: Number(d.organicImpression) || 0,
								adImpression: Number(d.adImpression) || 0,
								srpClick: Number(d.srpClick) || 0,
								searchVolume: Number(d.searchVolume) || 0
							}
							// 합산 누적
							if (!merged[kw][d.date]) merged[kw][d.date] = {
								date: d.date, impression: 0, organicImpression: 0, adImpression: 0,
								srpClick: 0, searchVolume: 0
							}
							merged[kw][d.date].impression += Number(d.impression) || 0
							merged[kw][d.date].organicImpression += Number(d.organicImpression) || 0
							merged[kw][d.date].adImpression += Number(d.adImpression) || 0
							merged[kw][d.date].srpClick += Number(d.srpClick) || 0
							const sv = Number(d.searchVolume) || 0
							if (sv > merged[kw][d.date].searchVolume) merged[kw][d.date].searchVolume = sv
						})
					})
				}

				// 4. 합산(vendorItemId='') + 옵션별 row 저장
				const meta = {
					productName: (r1.info && r1.info.productName) || item.itemName || '',
					imagePath: (r1.info && (r1.info.imagePath || r1.info.imgUrl)) || item.image || '',
					itemId: options[0].itemId,
					leafCategoryCode: options[0].leafCategoryCode || (r1.info && r1.info.leafCategoryCode) || item.leafCategoryCode || ''
				}
				let savedKw = 0
				// 합산 저장
				for (const kw of Object.keys(merged)) {
					const dailyData = Object.values(merged[kw]).sort((a, b) => new Date(a.date) - new Date(b.date))
					await axios.post('/api/analysis/saveExposureTrace', {
						productId: item.productId, keyword: kw, vendorItemId: '',
						productName: meta.productName, imagePath: meta.imagePath,
						itemId: meta.itemId, leafCategoryCode: meta.leafCategoryCode,
						ownership: item.ownership, keywordType: 'top10', dailyData: dailyData
					})
					savedKw++
				}
				// 옵션별 저장 (펼치기용)
				for (const vid of Object.keys(byOption)) {
					const optMatch = options.find(o => o.vendorItemId === vid) || options[0]
					for (const kw of Object.keys(byOption[vid])) {
						const dailyData = Object.values(byOption[vid][kw]).sort((a, b) => new Date(a.date) - new Date(b.date))
						await axios.post('/api/analysis/saveExposureTrace', {
							productId: item.productId, keyword: kw, vendorItemId: vid,
							productName: meta.productName, imagePath: meta.imagePath,
							itemId: optMatch.itemId, leafCategoryCode: optMatch.leafCategoryCode || meta.leafCategoryCode,
							ownership: item.ownership, keywordType: 'top10', dailyData: dailyData
						})
					}
				}
				// 5. 키워드는 영구 보존 — 7일 발견에서 빠진 옛 키워드도 삭제하지 않고
				//    누적 히스토리를 유지한다. 새 키워드는 위에서 추가만 됨.
				// 6. 키워드별 Top 10 순위 캐시 (옵션 — collectRanking ON일 때만)
				const lcc = options[0].leafCategoryCode || (r1.info && r1.info.leafCategoryCode) || item.leafCategoryCode
				let rankFetched = 0, rankCached = 0
				console.log('[수집-6 순위 단계]', {
					productId: item.productId,
					doRanking: doRanking,
					lcc: lcc,
					targetKeywordsCount: targetKeywords.length,
					targetKeywords: targetKeywords
				})
				if (!doRanking) {
					console.log('  → doRanking false. 순위 수집 SKIP. (⚙ 설정에서 ON 가능)')
				} else if (!lcc) {
					console.log('  → lcc 없음. 순위 수집 불가. GrowthDB에 displayCategoryCode 확인 필요.')
				} else if (targetKeywords.length === 0) {
					console.log('  → targetKeywords 빈 배열. 7일 발견 결과 없음.')
				}
				let autoSaved = 0
				if (doRanking && lcc && targetKeywords.length > 0) {
					// 내 itemId 집합 (옵션 전부 + r1.info 포함) — auto 저장 시 본인 SKIP용
					const myItemIdSet = new Set(
						[item.itemId, (r1.info && r1.info.itemId)]
							.concat(options.map(o => o.itemId))
							.filter(Boolean)
							.map(String)
					)
					// 1단계: 키워드별 multi date-wise → 메모리 누적
					const allAuto = {}  // { kw: { iid: { date: row } } }
					for (const kw of targetKeywords) {
						const cached = this.rankByKeyCat[kw + '|' + String(lcc)]
						let top10ForKw = (cached && Array.isArray(cached.top10)) ? cached.top10 : null
						if (this._isCacheFresh(cached) && top10ForKw && top10ForKw.length > 0) {
							rankCached++
							console.log('  - 캐시 hit:', kw)
						} else {
							this.progressMessage = `[${item.itemName || item.productId}] 키워드 순위 조회: ${kw}`
							const rr = await this._runKeywordRankItems(kw, lcc)
							console.log('  - 호출 결과:', kw, rr)
							if (rr.ok) {
								rankFetched++
								const fresh = this.rankByKeyCat[kw + '|' + String(lcc)]
								top10ForKw = (fresh && Array.isArray(fresh.top10)) ? fresh.top10 : null
							}
							await new Promise(r => setTimeout(r, 200))
						}

						if (!top10ForKw || top10ForKw.length === 0) continue
						const topItemIds = top10ForKw.map(t => t && String(t.itemId)).filter(Boolean)
						const allIds = Array.from(new Set([...topItemIds, ...Array.from(myItemIdSet)]))
						if (allIds.length === 0) continue
						this.progressMessage = `[${item.itemName || item.productId}] 경쟁사 30일 트래픽: ${kw}`
						const dr = await this._runKeywordItemsDateWise30(kw, lcc, allIds.join(','))
						if (!dr.ok) {
							console.warn('  - date-wise multi 실패:', kw, dr.error)
							continue
						}
						console.log('[date-wise multi]', kw, '요청', allIds.length, '개:', allIds, '→ 응답 unique itemIds:', Array.from(new Set(Object.values(dr.data || {}).flat().map(e => e && e.keywordItemDetail && String(e.keywordItemDetail.itemId)).filter(Boolean))))
						const perItem = {}
						Object.keys(dr.data).forEach(date => {
							const arr = Array.isArray(dr.data[date]) ? dr.data[date] : []
							arr.forEach(entry => {
								const detail = entry && entry.keywordItemDetail
								const metric = entry && entry.keywordMetric
								if (!detail || detail.itemId === undefined || detail.itemId === null) return
								const iid = String(detail.itemId)
								if (myItemIdSet.has(iid)) return  // 내 상품은 이미 mine으로 저장됨
								if (!perItem[iid]) perItem[iid] = {}
								perItem[iid][date] = {
									date: date,
									impression: Number(metric && metric.impression) || 0,
									organicImpression: Number(metric && metric.organicImpression) || 0,
									adImpression: Number(metric && metric.adImpression) || 0,
									srpClick: Number(metric && metric.srpClick) || 0,
									searchVolume: Number(metric && metric.searchVolume) || 0
								}
							})
						})
						allAuto[kw] = perItem
						await new Promise(r => setTimeout(r, 200))
					}

					// 2단계: 모든 키워드의 unique itemIds 모아 item-winner 1회 호출 → 메타(productName, imageUrl)
					const uniqueAutoIds = new Set()
					Object.values(allAuto).forEach(byItem => {
						Object.keys(byItem).forEach(iid => uniqueAutoIds.add(iid))
					})
					// item-winner는 Akamai sec-if-cpt challenge로 차단 — 임시 비활성.
					// productName 없이 저장 → 차트1 legend는 fallback 'Top N' / '#N' 라벨로 표시.
					// 추후 다른 endpoint (itemId→productName)로 갈아끼울 때 활성화.
					const itemMetaMap = {}

					// 3단계: itemId별 ownership='auto' 저장 (productName/imagePath 메타 반영)
					for (const kw of Object.keys(allAuto)) {
						const perItem = allAuto[kw]
						for (const iid of Object.keys(perItem)) {
							const dailyData = Object.values(perItem[iid]).sort((a, b) => new Date(a.date) - new Date(b.date))
							if (dailyData.length === 0) continue
							const m = itemMetaMap[iid] || itemMetaMap[Number(iid)] || {}
							try {
								await axios.post('/api/analysis/saveExposureTrace', {
									productId: iid, keyword: kw, vendorItemId: '',
									productName: m.productName || m.itemName || '',
									imagePath: m.imageUrl || '',
									itemId: iid, leafCategoryCode: String(lcc),
									ownership: 'auto', keywordType: 'auto', dailyData: dailyData
								})
								autoSaved++
							} catch (e) {
								console.warn('  - auto 저장 실패:', kw, iid, e && e.message)
							}
						}
					}
				}
				const rankMsg = doRanking ? `, 순위 ${rankFetched}신규/${rankCached}캐시, 경쟁사 ${autoSaved}건` : ''
				this.progressMessage = `[${item.itemName || item.productId}] 완료 (옵션 ${okOptions}/${options.length}, 키워드 ${savedKw}${rankMsg})`
				return { ok: true }
			} catch (e) {
				console.error('통합 수집 실패:', e)
				this.progressMessage = '수집 실패'
				return { ok: false, error: e.message }
			} finally {
				this.isCollecting = false
				await this.loadAll()
			}
		},

		// 완성 판정: 최신 데이터는 '어제' 날짜(오늘은 쿠팡 미제공). 데이터 1일 지연 허용
		// → 최근 2일 내 dailyData가 한 키워드라도 있으면 완성. 하나도 없으면(수집 실패/누락)
		// 재수집 대상. lastFetchedAt 날짜비교는 부분실패를 못 걸러내 폐기.
		_isCollectedComplete(item) {
			if (!item || !Array.isArray(item.keywords) || item.keywords.length === 0) return false
			const cutoff = new Date(Date.now() - 2 * 86400000).toISOString().split('T')[0]
			return item.keywords.some(kw => {
				const dd = Array.isArray(kw.dailyData) ? kw.dailyData : []
				return dd.some(d => d && d.date && d.date >= cutoff)
			})
		},
		async _bulkCollect(items) {
			if (this.isCollecting) return
			if (!items || items.length === 0) return
			// 최근 데이터가 채워진 상품은 기본 스킵. 단 사용자가 확인하면 강제 재수집 가능.
			const incompletes = items.filter(i => !this._isCollectedComplete(i))
			const skipped = items.length - incompletes.length
			let targets = incompletes
			if (skipped > 0) {
				const msg = incompletes.length === 0
					? `선택한 ${items.length}개 모두 최신 데이터가 있습니다. 다시 수집할까요?`
					: `이미 수집된 ${skipped}개 포함해서 ${items.length}개 전체 다시 수집할까요?\n[취소] = 비어있는 ${incompletes.length}개만 수집`
				const force = window.confirm(msg)
				if (force) {
					targets = items
				} else if (incompletes.length === 0) {
					return  // 미수집 없음 + 사용자가 강제 재수집 거부 → 종료
				}
			}
			let done = 0
			for (const item of targets) {
				this.progressMessage = `(${done + 1}/${targets.length}) ${item.itemName || item.productId}`
				await this.collectOne(item)
				done++
				await new Promise(r => setTimeout(r, 1500))
			}
			const stillSkipped = items.length - targets.length
			const skipMsg = stillSkipped > 0 ? ` (${stillSkipped}개 최신 데이터 보유, 스킵)` : ''
			this.progressMessage = `일괄 수집 완료 (${done}개)${skipMsg}`
		},
		bulkCollect() {
			// 옵션 row는 수집 대상 X (상품 단위 수집에 포함됨)
			const targets = this.tableSelected.filter(i => !i.isOption)
			this._bulkCollect(targets)
		},
		collectAllItems() {
			const productOnly = this.tableItems.filter(i => !i.isOption)
			if (!confirm(`${productOnly.length}개 상품 전체 수집을 진행할까요? 옵션마다 호출되어 시간이 오래 걸립니다.`)) return
			this._bulkCollect(productOnly.slice())
		},
		openProductModal(item) {
			if (!item || item.isOption) return
			this.selectedProductForModal = item
			this.$bvModal.show('productDetailModal')
		},
		// 모달 안에서 그 상품만 데이터 초기화
		async clearProductData(item) {
			if (!item || this.isCollecting) return
			if (!confirm(`"${item.itemName || item.productId}"의 노출추적 데이터를 모두 삭제할까요?`)) return
			if (!confirm('정말 삭제할까요? 복구 불가능합니다.')) return
			try {
				const r = await axios.post('/api/analysis/clearExposureByProducts', {
					productIds: [item.productId]
				})
				const data = r.data || {}
				alert(`삭제 완료: ${data.deleted || 0}건`)
				this.$bvModal.hide('productDetailModal')
				this.selectedProductForModal = null
				await this.loadAll()
			} catch (e) {
				console.error('상품 초기화 실패:', e)
				alert('초기화 실패: ' + (e.message || ''))
			}
		},
		// 모달 안에서 그 상품만 키워드 순위 수집 (다른 데이터는 그대로)
		async collectRankingsOnly(item) {
			if (this.isCollecting || !item) return
			const keywords = (item.keywords || []).map(k => k.keyword)
			if (keywords.length === 0) { alert('키워드가 없습니다. 먼저 [수집]을 진행하세요.'); return }
			const lcc = item.leafCategoryCode
			if (!lcc) { alert('카테고리 정보 없음. 먼저 [수집]을 한 번 진행하세요.'); return }
			this.isCollecting = true
			let fetched = 0, cached = 0
			try {
				for (const kw of keywords) {
					const c = this.rankByKeyCat[kw + '|' + String(lcc)]
					if (this._isCacheFresh(c)) { cached++; continue }
					this.progressMessage = `[${item.itemName || item.productId}] 순위 조회: ${kw}`
					const r = await this._runKeywordRankItems(kw, lcc)
					if (r.ok) fetched++
					await new Promise(r => setTimeout(r, 200))
				}
				this.progressMessage = `순위 수집 완료 (신규 ${fetched}, 캐시 ${cached})`
				await this.loadAll()
			} catch (e) {
				console.error('순위 수집 실패:', e)
			} finally {
				this.isCollecting = false
			}
		},
		// 키워드 dailyData 합 helper (모달 옵션 탭용)
		_sumKwMetric(kw, key) {
			if (!kw || !Array.isArray(kw.dailyData)) return 0
			return kw.dailyData.reduce((s, d) => s + (Number(d[key]) || 0), 0)
		},
		_kwCtr(kw) {
			const imp = this._sumKwMetric(kw, 'impression')
			const clk = this._sumKwMetric(kw, 'srpClick')
			return imp > 0 ? (clk / imp * 100).toFixed(2) + '%' : '-'
		},

		async clearSelectedData() {
			const products = this.tableSelected.filter(i => !i.isOption)
			if (products.length === 0) {
				alert('선택된 상품이 없습니다.')
				return
			}
			if (!confirm(`선택한 ${products.length}개 상품의 노출추적 데이터를 모두 삭제할까요?`)) return
			if (!confirm('정말 삭제할까요? 복구 불가능합니다.')) return
			try {
				const productIds = products.map(p => p.productId)
				const r = await axios.post('/api/analysis/clearExposureByProducts', { productIds })
				const data = r.data || {}
				alert(`삭제 완료: ${data.deleted || 0}건`)
				this.tableSelected = []
				await this.loadAll()
			} catch (e) {
				console.error('선택 초기화 실패:', e)
				alert('선택 초기화 실패: ' + (e.message || ''))
			}
		},

		async clearAllData() {
			if (!confirm('노출추적 모든 데이터(키워드 + 순서)를 삭제합니다. 계속할까요?')) return
			if (!confirm('정말로 모든 데이터를 삭제할까요? 복구 불가능합니다.')) return
			try {
				const r = await axios.post('/api/analysis/clearAllExposureData')
				const data = r.data || {}
				alert(`삭제 완료\n키워드 ${data.exposure || 0}건, 순서 ${data.order || 0}건`)
				await this.loadAll()
			} catch (e) {
				console.error('전체 초기화 실패:', e)
				alert('초기화 실패: ' + (e.message || ''))
			}
		},

		async addCompetitor() {
			const url = (this.competitorUrl || '').trim()
			const m = url.match(/coupang\.com\/vp\/products\/(\d+).*[?&]vendorItemId=(\d+)/)
			if (!m) { alert('올바른 쿠팡 상품 URL을 입력해 주세요.'); return }
			const tempItem = {
				productId: m[1], vendorItemId: m[2], ownership: 'competitor',
				itemName: '', image: '', itemId: '', leafCategoryCode: ''
			}
			this.competitorUrl = ''
			// 1. 레지스트리에 먼저 등록 (수집 실패해도 경쟁상품 등록은 유지)
			try {
				await axios.post('/api/analysis/registerCompetitor', {
					productId: tempItem.productId, vendorItemId: tempItem.vendorItemId
				})
			} catch (e) { console.error('경쟁상품 등록 실패:', e) }
			// 2. 데이터 수집
			await this.collectOne(tempItem)
			// 3. 수집으로 알아낸 메타(이름/이미지/카테고리)로 레지스트리 보강
			try {
				const meta = this.exposureItems.find(e =>
					String(e.productId) === String(tempItem.productId) && (e.productName || e.imagePath))
				if (meta) {
					await axios.post('/api/analysis/registerCompetitor', {
						productId: tempItem.productId, vendorItemId: tempItem.vendorItemId,
						productName: meta.productName || '', imagePath: meta.imagePath || '',
						itemId: meta.itemId || '', leafCategoryCode: meta.leafCategoryCode || ''
					})
					await this.loadAll()
				}
			} catch (e) { console.error('경쟁상품 메타 보강 실패:', e) }
		},
		async removeCompetitor(item) {
			if (!confirm(`"${item.itemName || item.productId}" 경쟁상품을 등록 해제하고 모든 데이터를 삭제할까요?`)) return
			try {
				await axios.post('/api/analysis/removeCompetitorProduct', {
					productId: item.productId
				})
				await this.loadAll()
			} catch (e) { console.error('경쟁상품 제거 실패:', e) }
		},

		async promptAddKeyword(item) {
			const kw = prompt(`"${item.itemName || item.productId}"에 추가할 키워드:`)
			const trimmed = (kw || '').trim()
			if (!trimmed) return
			this.isCollecting = true
			try {
				await axios.post('/api/analysis/addManualKeyword', {
					productId: item.productId, keyword: trimmed,
					ownership: item.ownership, productName: item.itemName || '',
					imagePath: item.image || '', itemId: item.itemId || '',
					leafCategoryCode: item.leafCategoryCode || ''
				})
				// 캐시(itemId/leafCategoryCode)가 있으면 즉시 데일리 채우기
				if (item.itemId && item.leafCategoryCode) {
					const r = await this._runAddKeywordDaily(item, trimmed)
					if (!r.ok) console.warn('addKeywordToTrace 실패:', r.error)
				} else {
					this.progressMessage = '키워드 메타 추가 완료. 데일리는 다음 [수집] 시 채워집니다.'
				}
				// collectRanking ON이면 그 키워드의 Top10 순위도 같이 (24h 캐시 hit이면 스킵)
				if (this.collectRanking && item.leafCategoryCode) {
					const cached = this.rankByKeyCat[trimmed + '|' + String(item.leafCategoryCode)]
					if (!this._isCacheFresh(cached)) {
						this.progressMessage = `[${trimmed}] 키워드 순위 조회...`
						const rr = await this._runKeywordRankItems(trimmed, item.leafCategoryCode)
						if (!rr.ok) console.warn('키워드 순위 조회 실패:', rr.error)
					}
				}
				await this.loadAll()
			} catch (e) {
				console.error('수동 키워드 추가 실패:', e)
			} finally {
				this.isCollecting = false
			}
		},
		async deleteKeyword(item, kw) {
			if (!confirm(`"${kw.keyword}" 키워드를 삭제할까요?`)) return
			try {
				await axios.post('/api/analysis/deleteExposureTrace', {
					productId: item.productId, keyword: kw.keyword
				})
				await this.loadAll()
			} catch (e) { console.error(e) }
		},

		openChart(item, kw) {
			if (!kw.dailyData || kw.dailyData.length === 0) return
			const sorted = kw.dailyData.slice().sort((a, b) => new Date(a.date) - new Date(b.date))
			const dates = sorted.map(d => d.date)
			const series = [
				{ name: '총 노출', data: sorted.map(d => Number(d.impression) || 0) },
				{ name: '자연 노출', data: sorted.map(d => Number(d.organicImpression) || 0) },
				{ name: '광고 노출', data: sorted.map(d => Number(d.adImpression) || 0) },
				{ name: '클릭', data: sorted.map(d => Number(d.srpClick) || 0) }
			]
			this.chartTitle = `${item.itemName || item.productId} - "${kw.keyword}" 추이`
			this.chartOptions = {
				series: series,
				chart: { type: 'line', height: 400, zoom: { enabled: true } },
				stroke: { width: 2, curve: 'smooth' },
				xaxis: { categories: dates, type: 'category' },
				yaxis: { title: { text: '수치' } },
				dataLabels: { enabled: false },
				legend: { position: 'top' }
			}
			this.$bvModal.show('exposureChartModal')
		}
	}
}
</script>

<style scoped>
.kw-row {
	padding: 4px 6px;
	border-bottom: 1px dashed #eee;
	height: 28px;
	line-height: 20px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	box-sizing: border-box;
	position: relative;
}
.kw-row:last-child {
	border-bottom: none;
}
.kw-row .kw-delete {
	display: none;
	position: absolute;
	right: 6px;
	top: 50%;
	transform: translateY(-50%);
	cursor: pointer;
	color: #dc3545;
	font-size: 14px;
}
.kw-row:hover .kw-delete {
	display: inline-block;
}
.kw-row.stat-row {
	background: #f6f8fa;
	font-weight: 600;
	color: #495057;
}
/* 설정 드롭다운 (⚙) - 테마 색 영향 제거 */
.settings-dropdown >>> .btn {
	background: #f8f9fa;
	border: 1px solid #dee2e6;
	color: #6c757d;
}
.settings-dropdown >>> .btn:hover {
	background: #e9ecef;
	border-color: #adb5bd;
}
/* dropdown-form을 dropdown-item과 시각적으로 통일 */
.settings-dropdown >>> .topn-form {
	padding: 0.25rem 1.5rem;
	font-size: 0.875rem;
	line-height: 1.5;
	min-width: 240px;
}
/* 상품명 클릭 가능 링크 */
.product-name-link {
	color: #0d6efd;
	font-weight: 600;
}
.product-name-link:hover {
	text-decoration: underline;
}
.product-thumb:hover {
	box-shadow: 0 0 0 2px #0d6efd;
}
/* 가로 stacked metric (노출 / 클릭 / CTR) */
.metric-inline {
	display: inline-flex;
	gap: 6px;
	align-items: center;
}
.metric-inline .m-imp {
	color: #495057;
	font-weight: 600;
}
.metric-inline .m-clk {
	color: #0d6efd;
}
.metric-inline .m-ctr {
	color: #6f42c1;
	font-size: 90%;
}

/* 심층 경쟁분석 슬라이더 — 눈금 + 5일 간격 날짜 라벨 + thumb 위 현재 시점 */
/* padding-left: 차트1의 Y축 라벨 영역과 좌측 정렬 보정 */
/* padding-top: 현재 시점 라벨이 슬라이더 thumb·트랙과 겹치지 않도록 여유 */
.competitor-slider-wrap {
	position: relative;
	padding-top: 24px;
	padding-bottom: 14px;
	padding-left: 20px;
}
.competitor-slider-wrap .competitor-range {
	height: 4px;
}
.competitor-slider-wrap .slider-current-label {
	position: absolute;
	top: 2px;
	transform: translateX(-50%);
	font-size: 10px;
	font-weight: 600;
	color: #0d6efd;
	white-space: nowrap;
	pointer-events: none;
	line-height: 1;
}
.competitor-slider-ticks {
	margin-top: 2px;
	padding: 0 8px;
	position: relative;
	pointer-events: none;
}
.competitor-slider-ticks .tick {
	position: relative;
	width: 1px;
	display: flex;
	justify-content: center;
	flex-shrink: 0;
}
.competitor-slider-ticks .tick-bar {
	width: 1px;
	height: 4px;
	background: #adb5bd;
}
.competitor-slider-ticks .tick-major .tick-bar {
	height: 7px;
	background: #495057;
}
.competitor-slider-ticks .tick-label {
	position: absolute;
	top: 8px;
	left: 50%;
	transform: translateX(-50%);
	font-size: 9px;
	color: #6c757d;
	white-space: nowrap;
}
</style>

<style>
/* 상품 상세 모달 — 화면 폭 90% (b-modal modal-class="modal-fullscreen-90") */
.modal-fullscreen-90 .modal-dialog {
	max-width: 90vw !important;
	width: 90vw !important;
	margin: 1.5rem auto;
}
</style>
